import { useState, useMemo, useCallback, useRef } from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ComposedChart, Area, ReferenceLine } from "recharts";

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════════════════════
const T = {
  bg: "#070d1a", card: "#0d1826", card2: "#111f33", border: "#1a3352",
  primary: "#00c2ff", secondary: "#00e5a0", accent: "#ffb800",
  danger: "#ff4d6d", purple: "#b388ff", muted: "#4a6585",
  text: "#e8f4ff", sub: "#7aa5c8", dim: "#3d5a7a",
};
const PALETTE = ["#00c2ff","#00e5a0","#ffb800","#b388ff","#ff4d6d","#29b6f6","#4db6ac","#ff8a65"];

// ═══════════════════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════════════════
const fmt = (n, short=true) => {
  if (!isFinite(n) || isNaN(n)) return "₹0";
  const s = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (short) {
    if (a >= 1e7) return s+"₹"+(a/1e7).toFixed(2)+" Cr";
    if (a >= 1e5) return s+"₹"+(a/1e5).toFixed(2)+" L";
  }
  return s+"₹"+Math.round(a).toLocaleString("en-IN");
};
const pct = n => (!isFinite(n)||isNaN(n)?0:n).toFixed(2)+"%";
const num = n => (!isFinite(n)||isNaN(n)?0:Math.round(n)).toLocaleString("en-IN");

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
const css = {
  toggle: (on,color=T.primary) => ({
    width:40,height:22,borderRadius:11,cursor:"pointer",flexShrink:0,
    background:on?color:"#1a3352",position:"relative",transition:"background .2s",
    border:"none",padding:0,
  }),
  toggleDot: (on) => ({
    width:16,height:16,borderRadius:8,background:"#fff",
    position:"absolute",top:3,left:on?21:3,transition:"left .2s",
    boxShadow:"0 1px 4px #0008",
  }),
};

function Toggle({label,sub,value,onChange,color=T.primary}) {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderTop:`1px solid ${T.border}`}}>
      <div><div style={{fontSize:12,color:T.sub}}>{label}</div>{sub&&<div style={{fontSize:10,color:T.muted}}>{sub}</div>}</div>
      <button style={css.toggle(value,color)} onClick={()=>onChange(!value)} role="switch" aria-checked={value}>
        <div style={css.toggleDot(value)}/>
      </button>
    </div>
  );
}

function Sl({label,min,max,step=1,value,onChange,pre="",suf="",tip}) {
  return (
    <div style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
        <span style={{fontSize:11,color:T.sub,fontWeight:600}}>{label}{tip&&<span title={tip} style={{marginLeft:4,color:T.muted,cursor:"help"}}>ⓘ</span>}</span>
        <div style={{display:"flex",alignItems:"center",gap:3}}>
          {pre&&<span style={{fontSize:11,color:T.muted}}>{pre}</span>}
          <input type="number" value={value} min={min} max={max} step={step}
            onChange={e=>onChange(parseFloat(e.target.value)||0)}
            style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,
              fontSize:13,fontWeight:700,padding:"2px 6px",width:85,textAlign:"right",outline:"none"}}/>
          {suf&&<span style={{fontSize:11,color:T.muted}}>{suf}</span>}
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={Math.min(Math.max(value,min),max)}
        onChange={e=>onChange(parseFloat(e.target.value))}
        style={{width:"100%",accentColor:T.primary,cursor:"pointer",height:3}}/>
    </div>
  );
}

function Sel({label,value,onChange,options,small}) {
  return (
    <div style={{marginBottom:12}}>
      {label&&<div style={{fontSize:11,color:T.sub,marginBottom:5,fontWeight:600}}>{label}</div>}
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:7,
          color:T.text,fontSize:small?11:12,padding:"7px 10px",outline:"none",cursor:"pointer"}}>
        {options.map(o=><option key={o.v||o} value={o.v||o}>{o.l||o}</option>)}
      </select>
    </div>
  );
}

function Seg({value,onChange,options}) {
  return (
    <div style={{display:"flex",gap:4,marginBottom:12}}>
      {options.map(o=>(
        <button key={o.v||o} onClick={()=>onChange(o.v||o)} style={{
          flex:1,padding:"6px 0",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",
          border:`1px solid ${value===(o.v||o)?T.primary:T.border}`,
          background:value===(o.v||o)?T.primary+"22":"transparent",
          color:value===(o.v||o)?T.primary:T.sub,transition:"all .15s",
        }}>{o.l||o}</button>
      ))}
    </div>
  );
}

function RRow({label,value,hi,color,sub,indent,dim}) {
  const c = color||(hi?T.primary:T.text);
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
      padding:"7px "+(indent?"18px":"10px")+" 7px "+(indent?"20px":"10px"),
      borderRadius:7,marginBottom:3,
      background:hi?c+"12":"transparent",
      borderLeft:hi?`3px solid ${c}`:"3px solid transparent",
      opacity:dim?0.5:1}}>
      <div>
        <div style={{fontSize:11,color:T.sub}}>{label}</div>
        {sub&&<div style={{fontSize:10,color:T.muted,marginTop:1}}>{sub}</div>}
      </div>
      <div style={{fontSize:14,fontWeight:800,color:c,fontFamily:"'Courier New',monospace",whiteSpace:"nowrap"}}>{value}</div>
    </div>
  );
}

function Sec({icon,label,color}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,margin:"14px 0 6px",opacity:.9}}>
      <span style={{fontSize:13}}>{icon}</span>
      <span style={{fontSize:10,fontWeight:800,letterSpacing:1,textTransform:"uppercase",color:color||T.sub}}>{label}</span>
    </div>
  );
}

function Div() { return <div style={{height:1,background:T.border,margin:"14px 0"}}/>; }

function ChTabs({active,onChange,opts=["Bar","Line","Pie"]}) {
  return (
    <div style={{display:"flex",gap:4,marginBottom:8}}>
      {opts.map(t=>(
        <button key={t} onClick={()=>onChange(t)} style={{
          padding:"3px 11px",borderRadius:20,fontSize:10,fontWeight:700,cursor:"pointer",
          border:`1px solid ${active===t?T.primary:T.border}`,
          background:active===t?T.primary+"22":"transparent",
          color:active===t?T.primary:T.sub,
        }}>{t}</button>
      ))}
    </div>
  );
}

const CTip = ({active,payload,label}) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:7,padding:"8px 12px",fontSize:11}}>
      {label&&<div style={{color:T.sub,marginBottom:3}}>{label}</div>}
      {payload.map((p,i)=>(
        <div key={i} style={{color:p.color||p.fill,fontWeight:700}}>
          {p.name}: {typeof p.value==="number"&&p.value>999?fmt(p.value):p.value}
        </div>
      ))}
    </div>
  );
};

function InfoBox({text,color=T.primary}) {
  return <div style={{background:color+"11",border:`1px solid ${color}33`,borderRadius:7,padding:"8px 12px",fontSize:11,color,marginBottom:10,lineHeight:1.5}}>{text}</div>;
}

function TaxPanel({on,setOn,bracket,setBracket,type,setType,showType=true}) {
  return <>
    <Toggle label="Apply tax on returns" value={on} onChange={setOn} color={T.purple}/>
    {on&&<div style={{background:T.purple+"0d",border:`1px solid ${T.purple}33`,borderRadius:8,padding:"10px 12px",marginTop:6,marginBottom:4}}>
      <Sel label="Tax Bracket" value={bracket} onChange={v=>setBracket(parseFloat(v))}
        options={[{v:"0",l:"0% (Exempt)"},{v:"5",l:"5%"},{v:"20",l:"20%"},{v:"30",l:"30%"}]}/>
      {showType&&<Sel label="Investment Type" value={type} onChange={setType}
        options={[{v:"equity",l:"Equity (LTCG 12.5% above ₹1.25L)"},{v:"debt",l:"Debt (per slab)"},{v:"exempt",l:"PPF/EPF (Tax-free)"}]}/>}
    </div>}
  </>;
}

function calcTaxOnReturns(returns, bracket, type) {
  if (type==="exempt"||bracket===0) return 0;
  if (type==="equity") return Math.max(0,returns-125000)*0.125;
  return returns*(bracket/100);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 1: INVESTMENT & WEALTH
// ═══════════════════════════════════════════════════════════════════════════════

function SIPCalc() {
  const [M,setM]=useState(10000); const [R,setR]=useState(12); const [Y,setY]=useState(10);
  const [inf,setInf]=useState(5.09); const [su,setSu]=useState(0);
  const [showInf,setShowInf]=useState(true); const [taxOn,setTaxOn]=useState(false);
  const [tb,setTb]=useState(30); const [tt,setTt]=useState("equity");
  const [chart,setChart]=useState("Line");
  const res=useMemo(()=>{
    let inv=0,corp=0,P=M;
    const ld=[];
    for(let y=1;y<=Y;y++){
      for(let m=0;m<12;m++){inv+=P;corp=(corp+P)*(1+R/100/12);}
      P*=1+su/100;
      ld.push({year:`Y${y}`,Invested:Math.round(inv),Value:Math.round(corp),
        InflAdj:Math.round(corp/Math.pow(1+inf/100,y))});
    }
    const ret=corp-inv, inflAdj=corp/Math.pow(1+inf/100,Y);
    const realR=((1+R/100)/(1+inf/100)-1)*100;
    const tax=calcTaxOnReturns(ret,tb,tt);
    return {inv,corp,ret,inflAdj,realR,tax,postTax:corp-tax,postTaxInflAdj:(corp-tax)/Math.pow(1+inf/100,Y),ld};
  },[M,R,Y,inf,su,tb,tt]);
  return(<div>
    <Sl label="Monthly Investment" min={500} max={100000} step={500} value={M} onChange={setM} pre="₹"/>
    <Sl label="Expected Annual Return" min={1} max={30} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Investment Period" min={1} max={40} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Step-up SIP (Annual)" min={0} max={30} value={su} onChange={setSu} suf="%"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted returns" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <TaxPanel on={taxOn} setOn={setTaxOn} bracket={tb} setBracket={setTb} type={tt} setType={setTt}/>
    <Div/>
    <Sec icon="💰" label="Nominal Returns"/>
    <RRow label="Total Invested" value={fmt(res.inv)}/>
    <RRow label="Expected Returns" value={fmt(res.ret)}/>
    <RRow label="Maturity Value" value={fmt(res.corp)} hi/>
    {showInf&&<><Sec icon="📉" label="Inflation-Adjusted" color="#ff8a65"/>
      <RRow label="Purchasing Power Today" value={fmt(res.inflAdj)} hi color="#ff8a65"/>
      <RRow label="Real Return Rate" value={pct(res.realR)}/></>}
    {taxOn&&<><Sec icon="💸" label="After Tax" color={T.purple}/>
      <RRow label={`Tax (${tt==="equity"?"LTCG 12.5%":tb+"%"})`} value={fmt(res.tax)} color={T.danger}/>
      <RRow label="Post-tax Maturity" value={fmt(res.postTax)} hi color={T.purple}/>
      {showInf&&<RRow label="Post-tax Inflation-Adjusted" value={fmt(res.postTaxInflAdj)} indent/>}</>}
    <Div/>
    <ChTabs active={chart} onChange={setChart} opts={["Line","Bar","Pie"]}/>
    <ResponsiveContainer width="100%" height={180}>
      {chart==="Line"?<LineChart data={res.ld}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false} interval={Math.floor(Y/5)}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Invested" stroke={PALETTE[0]} dot={false} strokeWidth={2}/>
        <Line type="monotone" dataKey="Value" stroke={PALETTE[1]} dot={false} strokeWidth={2.5}/>
        {showInf&&<Line type="monotone" dataKey="InflAdj" stroke="#ff8a65" dot={false} strokeWidth={1.5} strokeDasharray="4 2"/>}
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>:chart==="Bar"?<BarChart data={[{name:"Invested",v:Math.round(res.inv)},{name:"Returns",v:Math.round(res.ret)},{name:"Maturity",v:Math.round(res.corp)}]}>
        <XAxis dataKey="name" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Bar dataKey="v" radius={[4,4,0,0]}>{[0,1,2].map(i=><Cell key={i} fill={PALETTE[i]}/>)}</Bar>
      </BarChart>:<PieChart>
        <Pie data={[{name:"Invested",v:Math.round(res.inv)},{name:"Returns",v:Math.round(res.ret)}]} dataKey="v" cx="50%" cy="50%" outerRadius={72} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
          {[0,1].map(i=><Cell key={i} fill={PALETTE[i]}/>)}
        </Pie><Tooltip content={<CTip/>}/>
      </PieChart>}
    </ResponsiveContainer>
  </div>);
}

function LumpsumCalc() {
  const [A,setA]=useState(100000); const [R,setR]=useState(12); const [Y,setY]=useState(10);
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const [taxOn,setTaxOn]=useState(false); const [tb,setTb]=useState(30); const [tt,setTt]=useState("equity");
  const [chart,setChart]=useState("Bar");
  const res=useMemo(()=>{
    const corp=A*Math.pow(1+R/100,Y), ret=corp-A;
    const inflAdj=corp/Math.pow(1+inf/100,Y), realR=((1+R/100)/(1+inf/100)-1)*100;
    const tax=calcTaxOnReturns(ret,tb,tt);
    const ld=Array.from({length:Y},(_,i)=>({year:`Y${i+1}`,Value:Math.round(A*Math.pow(1+R/100,i+1)),InflAdj:Math.round(A*Math.pow(1+R/100,i+1)/Math.pow(1+inf/100,i+1))}));
    return {corp,ret,inflAdj,realR,tax,postTax:corp-tax,ld};
  },[A,R,Y,inf,tb,tt]);
  return(<div>
    <Sl label="Investment Amount" min={10000} max={10000000} step={10000} value={A} onChange={setA} pre="₹"/>
    <Sl label="Expected Annual Return" min={1} max={30} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Investment Period" min={1} max={40} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted returns" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <TaxPanel on={taxOn} setOn={setTaxOn} bracket={tb} setBracket={setTb} type={tt} setType={setTt}/>
    <Div/>
    <RRow label="Investment" value={fmt(A)}/><RRow label="Returns" value={fmt(res.ret)}/><RRow label="Maturity Value" value={fmt(res.corp)} hi/>
    {showInf&&<><RRow label="Inflation-Adjusted" value={fmt(res.inflAdj)} hi color="#ff8a65"/><RRow label="Real Return Rate" value={pct(res.realR)}/></>}
    {taxOn&&<><RRow label="Tax" value={fmt(res.tax)} color={T.danger}/><RRow label="Post-tax Value" value={fmt(res.postTax)} hi color={T.purple}/></>}
    <Div/>
    <ChTabs active={chart} onChange={setChart} opts={["Bar","Line"]}/>
    <ResponsiveContainer width="100%" height={170}>
      {chart==="Bar"?<BarChart data={[{name:"Invested",v:A},{name:"Returns",v:Math.round(res.ret)},{name:"Maturity",v:Math.round(res.corp)}]}>
        <XAxis dataKey="name" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/><Bar dataKey="v" radius={[4,4,0,0]}>{[0,1,2].map(i=><Cell key={i} fill={PALETTE[i]}/>)}</Bar>
      </BarChart>:<LineChart data={res.ld}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false} interval={Math.floor(Y/5)}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Value" stroke={PALETTE[1]} dot={false} strokeWidth={2}/>
        {showInf&&<Line type="monotone" dataKey="InflAdj" stroke="#ff8a65" dot={false} strokeWidth={1.5} strokeDasharray="4 2"/>}
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>}
    </ResponsiveContainer>
  </div>);
}

function SWPCalc() {
  const [inv,setInv]=useState(1000000); const [wd,setWd]=useState(10000);
  const [R,setR]=useState(12); const [Y,setY]=useState(10);
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const res=useMemo(()=>{
    let bal=inv; const mr=R/100/12; let tot=0; const ld=[];
    for(let m=0;m<Y*12;m++){
      bal=bal*(1+mr)-wd; tot+=wd;
      if(m%12===11) ld.push({year:`Y${Math.floor(m/12)+1}`,Corpus:Math.max(0,Math.round(bal)),InflAdj:Math.max(0,Math.round(bal/Math.pow(1+inf/100,Math.floor(m/12)+1)))});
      if(bal<=0){bal=0;break;}
    }
    return {tot,final:Math.max(0,bal),ld};
  },[inv,wd,R,Y,inf]);
  return(<div>
    <Sl label="Total Investment" min={100000} max={100000000} step={100000} value={inv} onChange={setInv} pre="₹"/>
    <Sl label="Monthly Withdrawal" min={5000} max={1000000} step={1000} value={wd} onChange={setWd} pre="₹"/>
    <Sl label="Expected Annual Return" min={1} max={30} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Time Period" min={1} max={40} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted corpus" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <Div/>
    <RRow label="Total Withdrawn" value={fmt(res.tot)}/><RRow label="Remaining Corpus" value={fmt(res.final)} hi/><RRow label="Total Value" value={fmt(res.tot+res.final)}/>
    {res.final===0&&<InfoBox text="⚠️ Corpus depleted before end of period! Reduce withdrawal or increase investment." color={T.danger}/>}
    <Div/>
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={res.ld}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Corpus" stroke={T.accent} dot={false} strokeWidth={2}/>
        {showInf&&<Line type="monotone" dataKey="InflAdj" stroke="#ff8a65" dot={false} strokeWidth={1.5} strokeDasharray="4 2"/>}
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>
    </ResponsiveContainer>
  </div>);
}

function STPCalc() {
  const [src,setSrc]=useState(500000); const [trf,setTrf]=useState(20000);
  const [dur,setDur]=useState(12); const [sR,setSR]=useState(7); const [dR,setDR]=useState(12);
  const res=useMemo(()=>{
    let srcBal=src, dstBal=0, totTrf=0;
    const smr=sR/100/12, dmr=dR/100/12;
    for(let m=0;m<dur;m++){
      srcBal=srcBal*(1+smr); // grow first
      const t=Math.min(trf,srcBal); srcBal-=t; dstBal=(dstBal+t)*(1+dmr); totTrf+=t;
    }
    return {srcFinal:srcBal,dstFinal:dstBal,totTrf,total:srcBal+dstBal};
  },[src,trf,dur,sR,dR]);
  return(<div>
    <Sl label="Source Fund Amount" min={10000} max={10000000} step={10000} value={src} onChange={setSrc} pre="₹"/>
    <Sl label="Monthly Transfer Amount" min={1000} max={500000} step={1000} value={trf} onChange={setTrf} pre="₹"/>
    <Sl label="Transfer Duration (months)" min={1} max={60} value={dur} onChange={setDur} suf=" mo"/>
    <Sl label="Source Fund Return" min={1} max={20} step={.5} value={sR} onChange={setSR} suf="%"/>
    <Sl label="Destination Fund Return" min={1} max={30} step={.5} value={dR} onChange={setDR} suf="%"/>
    <Div/>
    <InfoBox text="STP gradually moves money from liquid/debt fund to equity, reducing timing risk."/>
    <RRow label="Amount Transferred" value={fmt(res.totTrf)}/><RRow label="Source Fund Balance" value={fmt(res.srcFinal)}/><RRow label="Destination Fund Value" value={fmt(res.dstFinal)}/><RRow label="Total Portfolio Value" value={fmt(res.total)} hi/>
  </div>);
}

function CAGRCalc() {
  const [init,setInit]=useState(100000); const [fin,setFin]=useState(200000);
  const [Y,setY]=useState(5); const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const [taxOn,setTaxOn]=useState(false); const [tb,setTb]=useState(30); const [tt,setTt]=useState("equity");
  const res=useMemo(()=>{
    const cagr=(Math.pow(fin/init,1/Y)-1)*100, abs=((fin-init)/init)*100, gain=fin-init;
    const inflAdj=fin/Math.pow(1+inf/100,Y), realCAGR=((1+cagr/100)/(1+inf/100)-1)*100;
    const tax=calcTaxOnReturns(gain,tb,tt);
    return {cagr,abs,gain,inflAdj,realCAGR,tax,postTax:fin-tax};
  },[init,fin,Y,inf,tb,tt]);
  return(<div>
    <Sl label="Initial Investment" min={1000} max={100000000} step={1000} value={init} onChange={setInit} pre="₹"/>
    <Sl label="Final Value" min={1000} max={100000000} step={1000} value={fin} onChange={setFin} pre="₹"/>
    <Sl label="Duration" min={1} max={50} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <TaxPanel on={taxOn} setOn={setTaxOn} bracket={tb} setBracket={setTb} type={tt} setType={setTt}/>
    <Div/>
    <RRow label="CAGR" value={pct(res.cagr)} hi/><RRow label="Absolute Return" value={pct(res.abs)}/><RRow label="Total Gain" value={fmt(res.gain)}/>
    {showInf&&<><RRow label="Real Value (Today's Money)" value={fmt(res.inflAdj)} hi color="#ff8a65"/><RRow label="Real CAGR" value={pct(res.realCAGR)}/></>}
    {taxOn&&<><RRow label="Tax on Gain" value={fmt(res.tax)} color={T.danger}/><RRow label="Post-tax Final" value={fmt(res.postTax)} hi color={T.purple}/></>}
  </div>);
}

function AbsReturnCalc() {
  const [init,setInit]=useState(100000); const [fin,setFin]=useState(150000); const [Y,setY]=useState(3);
  const [hasTime,setHasTime]=useState(true);
  const res=useMemo(()=>{
    const abs=((fin-init)/init)*100, gain=fin-init;
    const ann=hasTime?(Math.pow(fin/init,1/Y)-1)*100:null;
    return {abs,gain,ann};
  },[init,fin,Y,hasTime]);
  return(<div>
    <Sl label="Initial Investment" min={1000} max={100000000} step={1000} value={init} onChange={setInit} pre="₹"/>
    <Sl label="Final Value" min={1000} max={100000000} step={1000} value={fin} onChange={setFin} pre="₹"/>
    <Toggle label="Include time period (for annualized return)" value={hasTime} onChange={setHasTime}/>
    {hasTime&&<Sl label="Duration" min={1} max={50} value={Y} onChange={setY} suf=" yrs"/>}
    <Div/>
    <RRow label="Total Gain / Loss" value={fmt(res.gain)} color={res.gain>=0?T.secondary:T.danger}/>
    <RRow label="Absolute Return" value={pct(res.abs)} hi color={res.abs>=0?T.primary:T.danger}/>
    {res.ann!==null&&<RRow label="Annualized Return (CAGR)" value={pct(res.ann)} color={res.ann>=0?T.secondary:T.danger}/>}
  </div>);
}

function XIRRCalc() {
  const [flows,setFlows]=useState([
    {date:"2023-01-15",amt:50000,type:"invest"},{date:"2023-06-15",amt:75000,type:"invest"},
    {date:"2024-12-20",amt:30000,type:"withdraw"}
  ]);
  const [curVal,setCurVal]=useState(250000); const [curDate,setCurDate]=useState("2025-03-15");
  const addFlow=()=>setFlows([...flows,{date:"2024-01-01",amt:10000,type:"invest"}]);
  const xirr=useMemo(()=>{
    const cf=flows.map(f=>({d:new Date(f.date),v:f.type==="invest"?-f.amt:f.amt}));
    cf.push({d:new Date(curDate),v:curVal});
    if(cf.length<2) return null;
    let rate=0.1;
    for(let iter=0;iter<100;iter++){
      const t0=cf[0].d;
      let npv=0,dnpv=0;
      cf.forEach(({d,v})=>{
        const t=(d-t0)/(365*24*3600*1000);
        npv+=v/Math.pow(1+rate,t);
        dnpv-=t*v/Math.pow(1+rate,t+1);
      });
      const newR=rate-npv/dnpv;
      if(Math.abs(newR-rate)<1e-7) break;
      rate=newR;
    }
    return isFinite(rate)?rate*100:null;
  },[flows,curVal,curDate]);
  const totInv=flows.filter(f=>f.type==="invest").reduce((s,f)=>s+f.amt,0);
  const totWd=flows.filter(f=>f.type==="withdraw").reduce((s,f)=>s+f.amt,0);
  return(<div>
    <div style={{fontSize:11,color:T.sub,marginBottom:8,fontWeight:700}}>CASH FLOWS</div>
    {flows.map((f,i)=>(
      <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
        <input type="date" value={f.date} onChange={e=>{const n=[...flows];n[i]={...f,date:e.target.value};setFlows(n);}}
          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"4px 6px",flex:1,outline:"none"}}/>
        <input type="number" value={f.amt} onChange={e=>{const n=[...flows];n[i]={...f,amt:parseFloat(e.target.value)||0};setFlows(n);}}
          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"4px 6px",width:90,outline:"none"}}/>
        <select value={f.type} onChange={e=>{const n=[...flows];n[i]={...f,type:e.target.value};setFlows(n);}}
          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"4px 6px",outline:"none"}}>
          <option value="invest">Invest</option><option value="withdraw">Withdraw</option>
        </select>
        <button onClick={()=>setFlows(flows.filter((_,j)=>j!==i))} style={{background:T.danger+"22",border:`1px solid ${T.danger}44`,borderRadius:5,color:T.danger,fontSize:11,padding:"4px 7px",cursor:"pointer"}}>×</button>
      </div>
    ))}
    <button onClick={addFlow} style={{width:"100%",padding:"6px 0",borderRadius:7,border:`1px dashed ${T.border}`,background:"transparent",color:T.sub,fontSize:11,cursor:"pointer",marginBottom:10}}>+ Add Cash Flow</button>
    <div style={{display:"flex",gap:8,marginBottom:10}}>
      <div style={{flex:1}}>
        <div style={{fontSize:10,color:T.sub,marginBottom:4}}>Current Value</div>
        <input type="number" value={curVal} onChange={e=>setCurVal(parseFloat(e.target.value)||0)}
          style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:13,padding:"5px 8px",outline:"none",boxSizing:"border-box"}}/>
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:10,color:T.sub,marginBottom:4}}>As of Date</div>
        <input type="date" value={curDate} onChange={e=>setCurDate(e.target.value)}
          style={{width:"100%",background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:12,padding:"5px 8px",outline:"none",boxSizing:"border-box"}}/>
      </div>
    </div>
    <Div/>
    <RRow label="Total Invested" value={fmt(totInv)}/><RRow label="Total Withdrawn" value={fmt(totWd)}/><RRow label="Current Value" value={fmt(curVal)}/>
    <RRow label="XIRR" value={xirr!==null?pct(xirr):"Insufficient data"} hi color={xirr&&xirr>0?T.primary:T.danger}/>
  </div>);
}

function GoalCalc() {
  const GOALS=[{l:"Retirement",v:20000000},{l:"House Down Payment",v:3000000},{l:"Child Education",v:5000000},{l:"Wedding",v:2000000},{l:"Custom",v:1000000}];
  const [goal,setGoal]=useState("Retirement"); const [target,setTarget]=useState(20000000);
  const [yrs,setYrs]=useState(20); const [curSav,setCurSav]=useState(100000); const [R,setR]=useState(12);
  const [inf,setInf]=useState(6.5);
  const res=useMemo(()=>{
    const futureTarget=target*Math.pow(1+inf/100,yrs);
    const curGrowth=curSav*Math.pow(1+R/100,yrs);
    const gap=futureTarget-curGrowth;
    const mr=R/100/12, n=yrs*12;
    const sipNeeded=gap>0?gap*mr/((Math.pow(1+mr,n)-1)*(1+mr)):0;
    const lumpsumNeeded=gap>0?gap/Math.pow(1+R/100,yrs):0;
    return {futureTarget,curGrowth,gap,sipNeeded,lumpsumNeeded};
  },[target,yrs,curSav,R,inf]);
  return(<div>
    <Sel label="Goal" value={goal} onChange={v=>{setGoal(v);const g=GOALS.find(x=>x.l===v);if(g)setTarget(g.v);}} options={GOALS.map(g=>({v:g.l,l:g.l}))}/>
    <Sl label="Target Amount" min={100000} max={100000000} step={100000} value={target} onChange={setTarget} pre="₹"/>
    <Sl label="Years to Goal" min={1} max={40} value={yrs} onChange={setYrs} suf=" yrs"/>
    <Sl label="Current Savings" min={0} max={10000000} step={10000} value={curSav} onChange={setCurSav} pre="₹"/>
    <Sl label="Expected Return" min={1} max={30} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Inflation on Goal" min={0} max={15} step={.5} value={inf} onChange={setInf} suf="%"/>
    <Div/>
    <RRow label="Inflation-adjusted Target" value={fmt(res.futureTarget)} hi/>
    <RRow label="Current Savings Growth" value={fmt(res.curGrowth)}/>
    <RRow label="Gap to Fill" value={fmt(Math.max(0,res.gap))} color={res.gap>0?T.danger:T.secondary}/>
    <RRow label="Monthly SIP Needed" value={fmt(res.sipNeeded)} hi color={T.secondary}/>
    <RRow label="Lumpsum Needed Today" value={fmt(res.lumpsumNeeded)}/>
    {res.gap<=0&&<InfoBox text="✅ Your current savings will exceed your goal!" color={T.secondary}/>}
  </div>);
}

function EPFCalc() {
  const [basic,setBasic]=useState(30000); const [empPct,setEmpPct]=useState(12);
  const [curBal,setCurBal]=useState(100000); const [yrs,setYrs]=useState(25);
  const [inc,setInc]=useState(10); const [R,setR]=useState(8.15);
  const res=useMemo(()=>{
    let bal=curBal, salary=basic;
    for(let y=0;y<yrs;y++){
      const monthly=(salary*empPct/100)*2*12; // employee+employer
      const ann=monthly;
      for(let m=0;m<12;m++) bal=(bal+salary*empPct/100*2)*(1+R/100/12);
      salary*=1+inc/100;
    }
    const totContrib=(()=>{let s=basic,t=0;for(let y=0;y<yrs;y++){t+=s*empPct/100*2*12;s*=1+inc/100;}return t;})();
    return {corpus:bal,totContrib,interest:bal-curBal-totContrib};
  },[basic,empPct,curBal,yrs,inc,R]);
  return(<div>
    <Sl label="Basic Salary" min={10000} max={500000} step={1000} value={basic} onChange={setBasic} pre="₹"/>
    <Sl label="Employee Contribution %" min={12} max={20} value={empPct} onChange={setEmpPct} suf="%"/>
    <Sl label="Current EPF Balance" min={0} max={10000000} step={10000} value={curBal} onChange={setCurBal} pre="₹"/>
    <Sl label="Years to Retirement" min={1} max={40} value={yrs} onChange={setYrs} suf=" yrs"/>
    <Sl label="Salary Increment" min={0} max={20} step={.5} value={inc} onChange={setInc} suf="% pa"/>
    <Sl label="EPF Interest Rate" min={7} max={10} step={.05} value={R} onChange={setR} suf="%"/>
    <InfoBox text="Current EPF rate: 8.15% p.a. Employee+Employer both contribute 12% each."/>
    <Div/>
    <RRow label="Monthly Contribution (Each)" value={fmt(basic*empPct/100)}/>
    <RRow label="Total Contributions" value={fmt(res.totContrib)}/>
    <RRow label="Interest Earned" value={fmt(res.interest)}/>
    <RRow label="Retirement Corpus" value={fmt(res.corpus)} hi/>
  </div>);
}

function NPSCalc() {
  const [age,setAge]=useState(30); const [retAge,setRetAge]=useState(60);
  const [monthly,setMonthly]=useState(5000); const [R,setR]=useState(10);
  const [annuityPct,setAnnuityPct]=useState(40); const [annuityR,setAnnuityR]=useState(6);
  const res=useMemo(()=>{
    const yrs=retAge-age, mr=R/100/12, n=yrs*12;
    const corpus=monthly*((Math.pow(1+mr,n)-1)/mr)*(1+mr);
    const lumpsum=corpus*(1-annuityPct/100);
    const annuityCorpus=corpus*(annuityPct/100);
    const monthlyPension=annuityCorpus*(annuityR/100)/12;
    const taxBenefit=Math.min(monthly*12,150000)*0.3;
    return {corpus,lumpsum,annuityCorpus,monthlyPension,taxBenefit};
  },[age,retAge,monthly,R,annuityPct,annuityR]);
  return(<div>
    <Sl label="Current Age" min={18} max={55} value={age} onChange={setAge} suf=" yrs"/>
    <Sl label="Retirement Age" min={age+1} max={70} value={retAge} onChange={setRetAge} suf=" yrs"/>
    <Sl label="Monthly Contribution" min={500} max={100000} step={500} value={monthly} onChange={setMonthly} pre="₹"/>
    <Sl label="Expected Return" min={5} max={15} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Annuity Purchase %" min={40} max={100} value={annuityPct} onChange={setAnnuityPct} suf="%"/>
    <Sl label="Annuity Rate" min={4} max={10} step={.5} value={annuityR} onChange={setAnnuityR} suf="%"/>
    <Div/>
    <RRow label="Total Corpus at Retirement" value={fmt(res.corpus)} hi/>
    <RRow label="Lumpsum Withdrawal (60%)" value={fmt(res.lumpsum)}/>
    <RRow label="Annuity Corpus (40%)" value={fmt(res.annuityCorpus)}/>
    <RRow label="Monthly Pension" value={fmt(res.monthlyPension)} hi color={T.secondary}/>
    <RRow label="Annual Tax Benefit (80CCD)" value={fmt(res.taxBenefit)} color={T.purple}/>
    <InfoBox text="Min 40% must be used for annuity. Tax-free on 60% withdrawal at maturity."/>
  </div>);
}

function SSYCalc() {
  const [age,setAge]=useState(5); const [yearly,setYearly]=useState(50000); const [R,setR]=useState(8.2);
  const res=useMemo(()=>{
    const depositYrs=Math.min(15,21-age); // deposit for 15 yrs from opening
    const maturityAge=21;
    let bal=0, inv=0;
    for(let y=1;y<=21-age;y++){
      if(y<=depositYrs){bal=(bal+yearly)*(1+R/100);inv+=yearly;}
      else bal=bal*(1+R/100);
    }
    return {corpus:bal,invested:inv,interest:bal-inv,maturityAge:age+21-age};
  },[age,yearly,R]);
  return(<div>
    <Sl label="Daughter's Current Age" min={0} max={10} value={age} onChange={setAge} suf=" yrs"/>
    <Sl label="Annual Investment" min={250} max={150000} step={250} value={yearly} onChange={setYearly} pre="₹"/>
    <Sl label="Interest Rate" min={6} max={10} step={.1} value={R} onChange={setR} suf="%"/>
    <InfoBox text="Current SSY rate: 8.2% p.a. Account matures at 21 years of age. Tax-free under EEE status."/>
    <Div/>
    <RRow label="Deposit Years" value={`15 years (till age ${age+15})`}/>
    <RRow label="Total Invested" value={fmt(res.invested)}/>
    <RRow label="Interest Earned" value={fmt(res.interest)}/>
    <RRow label="Maturity Amount (at age 21)" value={fmt(res.corpus)} hi/>
    <RRow label="Tax Benefit under 80C" value={fmt(Math.min(yearly,150000)*0.3)} color={T.purple}/>
  </div>);
}

function FDCalc() {
  const [dep,setDep]=useState(100000); const [R,setR]=useState(7);
  const [months,setMonths]=useState(12); const [freq,setFreq]=useState("4");
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const [taxOn,setTaxOn]=useState(false); const [tb,setTb]=useState(30);
  const res=useMemo(()=>{
    const n=parseFloat(freq),t=months/12;
    const mat=dep*Math.pow(1+R/100/n,n*t), int=mat-dep;
    const ear=(Math.pow(1+R/100/n,n)-1)*100;
    const inflAdj=mat/Math.pow(1+inf/100,t);
    const tax=int*(tb/100), postTax=dep+int-tax;
    return {mat,int,ear,inflAdj,tax,postTax};
  },[dep,R,months,freq,inf,tb]);
  return(<div>
    <Sl label="Deposit Amount" min={1000} max={10000000} step={1000} value={dep} onChange={setDep} pre="₹"/>
    <Sl label="Interest Rate" min={1} max={12} step={.05} value={R} onChange={setR} suf="%"/>
    <Sl label="Time Period" min={3} max={120} value={months} onChange={setMonths} suf=" mo"/>
    <Seg value={freq} onChange={setFreq} options={[{v:"12",l:"Monthly"},{v:"4",l:"Quarterly"},{v:"1",l:"Annually"}]}/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <Toggle label="Apply TDS/Tax on interest" value={taxOn} onChange={setTaxOn} color={T.purple}/>
    {taxOn&&<div style={{background:T.purple+"0d",border:`1px solid ${T.purple}33`,borderRadius:8,padding:"10px 12px",marginTop:4,marginBottom:4}}>
      <InfoBox text="FD interest taxable as per slab. TDS @ 10% if interest > ₹40,000/year." color={T.sub}/>
      <Sel label="Tax Bracket" value={tb} onChange={v=>setTb(parseFloat(v))} options={[{v:"0",l:"0%"},{v:"10",l:"10%"},{v:"20",l:"20%"},{v:"30",l:"30%"}]}/>
    </div>}
    <Div/>
    <RRow label="Invested Amount" value={fmt(dep)}/><RRow label="Interest Earned" value={fmt(res.int)}/><RRow label="Maturity Amount" value={fmt(res.mat)} hi/><RRow label="Effective Annual Rate" value={pct(res.ear)}/>
    {showInf&&<RRow label="Inflation-Adjusted Value" value={fmt(res.inflAdj)} hi color="#ff8a65"/>}
    {taxOn&&<><RRow label={`Tax on Interest (${tb}%)`} value={fmt(res.tax)} color={T.danger}/><RRow label="Post-tax Maturity" value={fmt(res.postTax)} hi color={T.purple}/></>}
  </div>);
}

function PPFCalc() {
  const [yearly,setYearly]=useState(150000); const [yrs,setYrs]=useState(15); const [R,setR]=useState(7.1);
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const res=useMemo(()=>{
    let bal=0;const ld=[];
    for(let y=0;y<yrs;y++){bal=(bal+yearly)*(1+R/100);ld.push({year:`Y${y+1}`,Value:Math.round(bal),InflAdj:Math.round(bal/Math.pow(1+inf/100,y+1))});}
    const inv=yearly*yrs;
    return {corpus:bal,invested:inv,interest:bal-inv,inflAdj:bal/Math.pow(1+inf/100,yrs),ld};
  },[yearly,yrs,R,inf]);
  return(<div>
    <Sl label="Yearly Investment" min={500} max={150000} step={500} value={yearly} onChange={setYearly} pre="₹"/>
    <Sl label="Time Period" min={15} max={50} value={yrs} onChange={setYrs} suf=" yrs"/>
    <Sl label="Interest Rate" min={5} max={10} step={.05} value={R} onChange={setR} suf="%"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <InfoBox text="PPF rate: 7.1% p.a. (Q1 2025). Tax-free under EEE. Min tenure: 15 years."/>
    <Div/>
    <RRow label="Total Invested" value={fmt(res.invested)}/><RRow label="Interest Earned" value={fmt(res.interest)}/><RRow label="Maturity Amount" value={fmt(res.corpus)} hi/>
    {showInf&&<RRow label="Inflation-Adjusted" value={fmt(res.inflAdj)} hi color="#ff8a65"/>}
  </div>);
}

function RDCalc() {
  const [monthly,setMonthly]=useState(5000); const [R,setR]=useState(7); const [months,setMonths]=useState(24);
  const res=useMemo(()=>{
    const n=4,t=months/12; // quarterly compounding
    let mat=0;
    for(let m=1;m<=months;m++) mat=(mat+monthly)*Math.pow(1+R/100/n,n/12);
    const inv=monthly*months, int=mat-inv;
    const ear=(Math.pow(1+R/100/n,n)-1)*100;
    return {mat,inv,int,ear};
  },[monthly,R,months]);
  return(<div>
    <Sl label="Monthly Deposit" min={100} max={100000} step={100} value={monthly} onChange={setMonthly} pre="₹"/>
    <Sl label="Interest Rate" min={1} max={12} step={.05} value={R} onChange={setR} suf="%"/>
    <Sl label="Tenure" min={6} max={120} value={months} onChange={setMonths} suf=" mo"/>
    <Div/>
    <RRow label="Total Deposits" value={fmt(res.inv)}/><RRow label="Interest Earned" value={fmt(res.int)}/><RRow label="Maturity Amount" value={fmt(res.mat)} hi/><RRow label="Effective Annual Rate" value={pct(res.ear)}/>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 2: LOAN & DEBT
// ═══════════════════════════════════════════════════════════════════════════════
function HomeLoanCalc() {
  const [P,setP]=useState(5000000); const [R,setR]=useState(8.75); const [Y,setY]=useState(20);
  const [prepay,setPrepay]=useState(0); const [prepayType,setPrepayType]=useState("reduce_tenure");
  const [emiInc,setEmiInc]=useState(0); const [taxOn,setTaxOn]=useState(false); const [tb,setTb]=useState(30);
  const [showAmort,setShowAmort]=useState(false);
  const res=useMemo(()=>{
    const mr=R/100/12,n=Y*12;
    const emi=(P*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);
    const totPay=emi*n, totInt=totPay-P;
    // With increased EMI
    const newEmi=emi+emiInc;
    const newTenure=emiInc>0?Math.log(newEmi/(newEmi-P*mr))/Math.log(1+mr):n;
    const emiSaved=totPay-(newEmi*newTenure);
    // Prepayment
    let prepayNewTenure=n, prepayIntSaved=0;
    if(prepay>0){
      const remP=P-prepay;
      if(remP>0){
        prepayNewTenure=Math.log(emi/(emi-remP*mr))/Math.log(1+mr);
        prepayIntSaved=totPay-(emi*prepayNewTenure+prepay);
      }
    }
    // Tax benefit
    const annInt=Math.min(totInt/Y,200000), annPrin=Math.min(P/Y,150000);
    const taxSaved=(annInt+annPrin)*(tb/100);
    const effRate=R-(taxSaved/P)*100;
    // Amortization
    const amort=[];let bal=P;
    for(let y=1;y<=Math.min(Y,30);y++){
      let yPrin=0,yInt=0;
      for(let m=0;m<12;m++){const i=bal*mr;const p=emi-i;yPrin+=p;yInt+=i;bal-=p;}
      amort.push({year:y,emi:Math.round(emi*12),principal:Math.round(yPrin),interest:Math.round(yInt),closing:Math.max(0,Math.round(bal))});
    }
    return {emi,totPay,totInt,newTenure,emiSaved,prepayNewTenure,prepayIntSaved,taxSaved,effRate,amort};
  },[P,R,Y,prepay,emiInc,tb]);
  return(<div>
    <Sl label="Loan Amount" min={100000} max={100000000} step={100000} value={P} onChange={setP} pre="₹"/>
    <Sl label="Interest Rate" min={1} max={20} step={.05} value={R} onChange={setR} suf="%"/>
    <Sl label="Loan Tenure" min={1} max={30} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Increase EMI by" min={0} max={50000} step={500} value={emiInc} onChange={setEmiInc} pre="₹" tip="Increase EMI to reduce tenure"/>
    <Sl label="One-time Prepayment" min={0} max={10000000} step={50000} value={prepay} onChange={setPrepay} pre="₹"/>
    <Toggle label="Show tax benefits (80C + Sec 24b)" value={taxOn} onChange={setTaxOn} color={T.purple}/>
    {taxOn&&<div style={{background:T.purple+"0d",border:`1px solid ${T.purple}33`,borderRadius:8,padding:"10px 12px",marginTop:4}}>
      <Sel label="Tax Bracket" value={tb} onChange={v=>setTb(parseFloat(v))} options={[{v:"5",l:"5%"},{v:"20",l:"20%"},{v:"30",l:"30%"}]}/>
    </div>}
    <Div/>
    <RRow label="Monthly EMI" value={fmt(res.emi)} hi/><RRow label="Total Interest" value={fmt(res.totInt)}/><RRow label="Total Payable" value={fmt(res.totPay)}/>
    {emiInc>0&&<><Sec icon="⚡" label="With Increased EMI" color={T.secondary}/>
      <RRow label="New Tenure" value={`~${(res.newTenure/12).toFixed(1)} yrs`} color={T.secondary}/>
      <RRow label="Time Saved" value={`${((Y*12-res.newTenure)/12).toFixed(1)} yrs`}/>
      <RRow label="Interest Saved" value={fmt(Math.max(0,res.emiSaved))} color={T.secondary}/></>}
    {prepay>0&&<><Sec icon="💡" label="With Prepayment" color={T.accent}/>
      <RRow label="New Tenure" value={`~${(res.prepayNewTenure/12).toFixed(1)} yrs`}/>
      <RRow label="Interest Saved" value={fmt(Math.max(0,res.prepayIntSaved))} color={T.secondary}/></>}
    {taxOn&&<><Sec icon="💸" label="Tax Benefits" color={T.purple}/>
      <RRow label="Annual Tax Saved" value={fmt(res.taxSaved)} hi color={T.purple}/>
      <RRow label="Effective Interest Rate" value={pct(res.effRate)} sub="After tax savings"/></>}
    <Div/>
    <button onClick={()=>setShowAmort(!showAmort)} style={{width:"100%",padding:"7px 0",background:"transparent",border:`1px solid ${T.border}`,borderRadius:7,color:T.sub,fontSize:11,cursor:"pointer",fontWeight:700}}>
      {showAmort?"▲ Hide":"▼ Show"} Amortization Schedule
    </button>
    {showAmort&&<div style={{overflowX:"auto",marginTop:10}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
        <thead><tr style={{color:T.sub,borderBottom:`1px solid ${T.border}`}}>
          {["Yr","EMI","Principal","Interest","Closing"].map(h=><th key={h} style={{padding:"5px 6px",textAlign:"right",fontWeight:700}}>{h}</th>)}
        </tr></thead>
        <tbody>{res.amort.map(r=><tr key={r.year} style={{borderBottom:`1px solid ${T.border}22`,color:T.text}}>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.sub}}>{r.year}</td>
          <td style={{padding:"4px 6px",textAlign:"right"}}>{fmt(r.emi)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.secondary}}>{fmt(r.principal)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.danger}}>{fmt(r.interest)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",fontWeight:700}}>{fmt(r.closing)}</td>
        </tr>)}</tbody>
      </table>
    </div>}
  </div>);
}

function GenLoanCalc({title,defR=12,defY=5,defP=500000,noTax=true}) {
  const [P,setP]=useState(defP); const [R,setR]=useState(defR); const [Y,setY]=useState(defY);
  const [proc,setProc]=useState(1); const [prepay,setPrepay]=useState(0);
  const res=useMemo(()=>{
    const mr=R/100/12,n=Y*12;
    const emi=(P*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);
    const totPay=emi*n,totInt=totPay-P,procFee=P*proc/100;
    const effCost=((totInt+procFee)/P/Y)*100;
    let prepayNewT=n,intSaved=0;
    if(prepay>0){const rem=P-prepay;if(rem>0){prepayNewT=Math.log(emi/(emi-rem*mr))/Math.log(1+mr);intSaved=totPay-(emi*prepayNewT+prepay);}}
    return {emi,totPay,totInt,procFee,effCost,prepayNewT,intSaved};
  },[P,R,Y,proc,prepay]);
  return(<div>
    <Sl label="Loan Amount" min={10000} max={50000000} step={10000} value={P} onChange={setP} pre="₹"/>
    <Sl label="Interest Rate" min={5} max={36} step={.25} value={R} onChange={setR} suf="%"/>
    <Sl label="Tenure" min={1} max={30} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Processing Fee" min={0} max={5} step={.1} value={proc} onChange={setProc} suf="%"/>
    <Sl label="Prepayment Amount" min={0} max={5000000} step={10000} value={prepay} onChange={setPrepay} pre="₹"/>
    <Div/>
    <RRow label="Monthly EMI" value={fmt(res.emi)} hi/><RRow label="Total Interest" value={fmt(res.totInt)}/><RRow label="Processing Fee" value={fmt(res.procFee)}/><RRow label="Total Payable" value={fmt(res.totPay)}/><RRow label="Effective Cost per Year" value={pct(res.effCost)}/>
    {prepay>0&&<><RRow label="Interest Saved (Prepay)" value={fmt(Math.max(0,res.intSaved))} color={T.secondary}/><RRow label="New Tenure" value={`~${(res.prepayNewT/12).toFixed(1)} yrs`}/></>}
  </div>);
}

function CarLoanCalc() {
  const [price,setPrice]=useState(1000000); const [dp,setDp]=useState(200000);
  const [R,setR]=useState(9); const [Y,setY]=useState(5);
  const [ins,setIns]=useState(30000); const [maint,setMaint]=useState(15000);
  const res=useMemo(()=>{
    const loan=price-dp,mr=R/100/12,n=Y*12;
    const emi=(loan*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);
    const totInt=emi*n-loan, totIns=ins*Y, totMaint=maint*Y;
    const tco=price+totInt+totIns+totMaint;
    const deprec=price*Math.pow(0.85,Y); // ~15%/yr
    return {loan,emi,totInt,tco,deprec,totIns,totMaint};
  },[price,dp,R,Y,ins,maint]);
  return(<div>
    <Sl label="Car Price" min={200000} max={10000000} step={50000} value={price} onChange={setPrice} pre="₹"/>
    <Sl label="Down Payment" min={0} max={price} step={50000} value={dp} onChange={setDp} pre="₹"/>
    <Sl label="Interest Rate" min={5} max={20} step={.25} value={R} onChange={setR} suf="%"/>
    <Sl label="Tenure" min={1} max={7} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Annual Insurance" min={5000} max={200000} step={1000} value={ins} onChange={setIns} pre="₹"/>
    <Sl label="Annual Maintenance" min={5000} max={100000} step={1000} value={maint} onChange={setMaint} pre="₹"/>
    <Div/>
    <RRow label="Loan Amount" value={fmt(res.loan)}/><RRow label="Monthly EMI" value={fmt(res.emi)} hi/><RRow label="Total Interest" value={fmt(res.totInt)}/><RRow label="Total Insurance" value={fmt(res.totIns)}/><RRow label="Total Maintenance" value={fmt(res.totMaint)}/>
    <RRow label="Total Cost of Ownership" value={fmt(res.tco)} hi color={T.accent}/>
    <RRow label={`Car Value After ${Y} yrs`} value={fmt(res.deprec)} color={T.danger} sub="Approx. after depreciation"/>
  </div>);
}

function GoldLoanCalc() {
  const [grams,setGrams]=useState(50); const [purity,setPurity]=useState("22K");
  const [rate,setRate]=useState(6500); const [ltv,setLtv]=useState(75);
  const [R,setR]=useState(12); const [months,setMonths]=useState(12);
  const purityMap={"24K":1,"22K":0.916,"18K":0.75};
  const res=useMemo(()=>{
    const goldVal=grams*rate*purityMap[purity];
    const maxLoan=goldVal*ltv/100;
    const monthly=maxLoan*R/100/12;
    const totRepay=maxLoan+monthly*months;
    return {goldVal,maxLoan,monthly,totRepay};
  },[grams,purity,rate,ltv,R,months]);
  return(<div>
    <Sl label="Gold Weight" min={1} max={500} step={.5} value={grams} onChange={setGrams} suf="g"/>
    <Seg value={purity} onChange={setPurity} options={["24K","22K","18K"]}/>
    <Sl label="Gold Rate per gram" min={4000} max={10000} step={100} value={rate} onChange={setRate} pre="₹"/>
    <Sl label="LTV Ratio" min={50} max={90} step={5} value={ltv} onChange={setLtv} suf="%"/>
    <Sl label="Interest Rate" min={7} max={24} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Tenure" min={1} max={36} value={months} onChange={setMonths} suf=" mo"/>
    <Div/>
    <RRow label="Gold Market Value" value={fmt(res.goldVal)}/><RRow label="Maximum Loan (LTV)" value={fmt(res.maxLoan)} hi/><RRow label="Monthly Interest" value={fmt(res.monthly)}/><RRow label="Total Repayment" value={fmt(res.totRepay)}/>
  </div>);
}

function BalTransferCalc() {
  const [outstanding,setOut]=useState(2000000); const [curR,setCurR]=useState(9);
  const [remYrs,setRemYrs]=useState(15); const [newR,setNewR]=useState(7.5); const [procFee,setProcFee]=useState(0.5);
  const res=useMemo(()=>{
    const mr1=curR/100/12,n=remYrs*12;
    const emi1=(outstanding*mr1*Math.pow(1+mr1,n))/(Math.pow(1+mr1,n)-1);
    const mr2=newR/100/12;
    const emi2=(outstanding*mr2*Math.pow(1+mr2,n))/(Math.pow(1+mr2,n)-1);
    const fee=outstanding*procFee/100;
    const intSaved=(emi1-emi2)*n-fee;
    const breakeven=fee/(emi1-emi2);
    return {emi1,emi2,fee,intSaved,breakeven,monthlyDiff:emi1-emi2};
  },[outstanding,curR,remYrs,newR,procFee]);
  return(<div>
    <Sl label="Outstanding Loan" min={100000} max={50000000} step={100000} value={outstanding} onChange={setOut} pre="₹"/>
    <Sl label="Current Interest Rate" min={5} max={20} step={.25} value={curR} onChange={setCurR} suf="%"/>
    <Sl label="Remaining Tenure" min={1} max={30} value={remYrs} onChange={setRemYrs} suf=" yrs"/>
    <Sl label="New Lender's Rate" min={5} max={20} step={.25} value={newR} onChange={setNewR} suf="%"/>
    <Sl label="Processing Fee" min={0} max={3} step={.1} value={procFee} onChange={setProcFee} suf="%"/>
    <Div/>
    <RRow label="Current EMI" value={fmt(res.emi1)}/><RRow label="New EMI" value={fmt(res.emi2)} hi color={T.secondary}/><RRow label="Monthly Savings" value={fmt(res.monthlyDiff)} color={T.secondary}/><RRow label="Processing Fee (One-time)" value={fmt(res.fee)} color={T.danger}/><RRow label="Net Interest Saved" value={fmt(Math.max(0,res.intSaved))} hi color={T.secondary}/>
    <RRow label="Breakeven Period" value={`${Math.round(res.breakeven)} months`}/>
    {res.intSaved>0?<InfoBox text={`✅ Transfer saves ${fmt(res.intSaved)} overall. Breakeven in ${Math.round(res.breakeven)} months.`} color={T.secondary}/>:<InfoBox text="❌ Transfer not beneficial after processing fees." color={T.danger}/>}
  </div>);
}

function DebtConsolidationCalc() {
  const [debts,setDebts]=useState([{name:"Credit Card",amt:200000,rate:36},{name:"Personal Loan",amt:500000,rate:14},{name:"Car Loan",amt:300000,rate:9}]);
  const [consR,setConsR]=useState(12); const [consY,setConsY]=useState(5);
  const res=useMemo(()=>{
    const curEmi=debts.reduce((s,d)=>{const mr=d.rate/100/12,n=60;return s+(d.amt*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);},0);
    const totDebt=debts.reduce((s,d)=>s+d.amt,0);
    const mr=consR/100/12,n=consY*12;
    const consEmi=(totDebt*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);
    const curTotInt=debts.reduce((s,d)=>{const mr=d.rate/100/12,n=60,emi=(d.amt*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);return s+emi*n-d.amt;},0);
    const consInt=consEmi*n-totDebt;
    return {curEmi,consEmi,totDebt,curTotInt,consInt,saving:curEmi-consEmi,intSaved:curTotInt-consInt};
  },[debts,consR,consY]);
  return(<div>
    <div style={{fontSize:11,color:T.sub,fontWeight:700,marginBottom:8}}>YOUR DEBTS</div>
    {debts.map((d,i)=>(
      <div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
        <input value={d.name} onChange={e=>{const n=[...debts];n[i]={...d,name:e.target.value};setDebts(n);}} style={{flex:2,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"5px 7px",outline:"none"}}/>
        <input type="number" value={d.amt} onChange={e=>{const n=[...debts];n[i]={...d,amt:parseFloat(e.target.value)||0};setDebts(n);}} placeholder="Amount" style={{flex:2,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"5px 7px",outline:"none"}}/>
        <input type="number" value={d.rate} onChange={e=>{const n=[...debts];n[i]={...d,rate:parseFloat(e.target.value)||0};setDebts(n);}} placeholder="Rate%" style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:11,padding:"5px 7px",outline:"none"}}/>
        <button onClick={()=>setDebts(debts.filter((_,j)=>j!==i))} style={{background:T.danger+"22",border:`1px solid ${T.danger}44`,borderRadius:5,color:T.danger,fontSize:11,padding:"4px 7px",cursor:"pointer"}}>×</button>
      </div>
    ))}
    <button onClick={()=>setDebts([...debts,{name:"New Debt",amt:100000,rate:12}])} style={{width:"100%",padding:"6px 0",borderRadius:7,border:`1px dashed ${T.border}`,background:"transparent",color:T.sub,fontSize:11,cursor:"pointer",marginBottom:12}}>+ Add Debt</button>
    <Sl label="Consolidation Loan Rate" min={8} max={24} step={.5} value={consR} onChange={setConsR} suf="%"/>
    <Sl label="Consolidation Tenure" min={1} max={10} value={consY} onChange={setConsY} suf=" yrs"/>
    <Div/>
    <RRow label="Total Debt" value={fmt(res.totDebt)}/><RRow label="Current Total EMI" value={fmt(res.curEmi)}/><RRow label="Consolidated EMI" value={fmt(res.consEmi)} hi color={T.secondary}/><RRow label="Monthly Savings" value={fmt(res.saving)} color={res.saving>0?T.secondary:T.danger}/><RRow label="Interest Saved" value={fmt(Math.max(0,res.intSaved))} color={T.secondary}/>
  </div>);
}

function CreditCardPayoffCalc() {
  const [bal,setBal]=useState(200000); const [pay,setPay]=useState(15000);
  const res=useMemo(()=>{
    const r=0.03; // 3%/month = 36% p.a.
    let b=bal,months=0,totPaid=0,minMonths=0,minPaid=0;
    // With specified payment
    while(b>0&&months<300){b=b*(1+r)-pay;totPaid+=pay;months++;if(b<0)b=0;}
    // Minimum payment (3% of balance or ₹200)
    let bm=bal;
    while(bm>0&&minMonths<600){const mp=Math.max(bm*0.03,200);bm=bm*(1+r)-mp;minPaid+=mp;minMonths++;if(bm<0)bm=0;}
    const int=totPaid-bal;
    const minInt=minMonths>=600?Infinity:minPaid-bal;
    return {months,totPaid,int,minMonths,minInt};
  },[bal,pay]);
  return(<div>
    <Sl label="Outstanding Balance" min={10000} max={2000000} step={10000} value={bal} onChange={setBal} pre="₹"/>
    <Sl label="Monthly Payment" min={1000} max={500000} step={1000} value={pay} onChange={setPay} pre="₹"/>
    <InfoBox text="Credit card rate: 36% p.a. (3%/month). Minimum payment = 3% of balance." color={T.sub}/>
    <Div/>
    <RRow label="Payoff Period" value={`${res.months} months (${(res.months/12).toFixed(1)} yrs)`} hi/><RRow label="Total Paid" value={fmt(res.totPaid)}/><RRow label="Interest Paid" value={fmt(res.int)} color={T.danger}/>
    <Sec icon="⚠️" label="With Minimum Payment" color={T.danger}/>
    <RRow label="Payoff Period" value={res.minMonths>=600?"Never (balance grows!)":"~"+Math.round(res.minMonths)+" months"} color={T.danger}/>
    {res.int<res.minInt&&<InfoBox text={`Your payment saves ${fmt(res.minInt-res.int)} in interest vs minimum payment!`} color={T.secondary}/>}
  </div>);
}

function LoanAffordCalc() {
  const [income,setIncome]=useState(100000); const [existEmi,setExistEmi]=useState(20000);
  const [R,setR]=useState(9); const [Y,setY]=useState(20); const [dti,setDti]=useState(40);
  const res=useMemo(()=>{
    const maxEmi=(income*dti/100)-existEmi;
    const mr=R/100/12,n=Y*12;
    const maxLoan=maxEmi>0?maxEmi*(Math.pow(1+mr,n)-1)/(mr*Math.pow(1+mr,n)):0;
    return {maxEmi:Math.max(0,maxEmi),maxLoan:Math.max(0,maxLoan),dtiNow:(existEmi/income)*100};
  },[income,existEmi,R,Y,dti]);
  return(<div>
    <Sl label="Monthly Income" min={20000} max={500000} step={5000} value={income} onChange={setIncome} pre="₹"/>
    <Sl label="Existing EMIs" min={0} max={100000} step={1000} value={existEmi} onChange={setExistEmi} pre="₹"/>
    <Sl label="Interest Rate" min={7} max={20} step={.25} value={R} onChange={setR} suf="%"/>
    <Sl label="Desired Tenure" min={1} max={30} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="DTI Limit" min={30} max={60} value={dti} onChange={setDti} suf="%"/>
    <Div/>
    <RRow label="Current DTI" value={pct(res.dtiNow)} color={res.dtiNow>40?T.danger:T.secondary}/>
    <RRow label="Available EMI Capacity" value={fmt(res.maxEmi)}/><RRow label="Maximum Loan Eligible" value={fmt(res.maxLoan)} hi/>
    {res.maxLoan<=0&&<InfoBox text="❌ Existing EMIs exceed DTI limit. Reduce existing debt first." color={T.danger}/>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 3: INSURANCE
// ═══════════════════════════════════════════════════════════════════════════════
function TermInsCalc() {
  const [age,setAge]=useState(30); const [income,setIncome]=useState(1200000);
  const [loans,setLoans]=useState(3000000); const [retAge,setRetAge]=useState(60);
  const [spouse,setSpouse]=useState(0);
  const res=useMemo(()=>{
    const yrs=retAge-age;
    const hlv=income*yrs; // Human Life Value
    const recommended=hlv+loans-spouse*yrs;
    const premium=Math.round(recommended/1000)*8; // rough estimate ₹8 per ₹1000
    return {hlv,recommended:Math.max(recommended,0),premium};
  },[age,income,loans,retAge,spouse]);
  return(<div>
    <Sl label="Current Age" min={18} max={55} value={age} onChange={setAge} suf=" yrs"/>
    <Sl label="Annual Income" min={300000} max={10000000} step={100000} value={income} onChange={setIncome} pre="₹"/>
    <Sl label="Total Existing Loans" min={0} max={50000000} step={100000} value={loans} onChange={setLoans} pre="₹"/>
    <Sl label="Retirement Age" min={age+1} max={70} value={retAge} onChange={setRetAge} suf=" yrs"/>
    <Sl label="Spouse Annual Income" min={0} max={5000000} step={50000} value={spouse} onChange={setSpouse} pre="₹"/>
    <InfoBox text="Coverage = (Annual income × years to retirement) + Existing loans - Spouse income offset"/>
    <Div/>
    <RRow label="Human Life Value" value={fmt(res.hlv)}/><RRow label="Recommended Coverage" value={fmt(res.recommended)} hi/><RRow label="Est. Annual Premium" value={fmt(res.premium)} sub="Rough estimate, actual varies by insurer"/>
    <InfoBox text="Rule of thumb: Coverage = 10-12× annual income. Add all outstanding loans." color={T.sub}/>
  </div>);
}

function ULIPCalc() {
  const [prem,setPrem]=useState(100000); const [term,setTerm]=useState(15);
  const [eqPct,setEqPct]=useState(70); const [eqR,setEqR]=useState(12); const [debtR,setDebtR]=useState(7);
  const [charges,setCharges]=useState(2.5);
  const res=useMemo(()=>{
    const blended=(eqPct/100*eqR+(100-eqPct)/100*debtR)/100;
    const netR=blended-charges/100;
    let corpus=0,totPrem=0;
    for(let y=0;y<term;y++){corpus=(corpus+prem)*(1+netR);totPrem+=prem;}
    const totCharges=totPrem*(charges/100)*term;
    const cagr=(Math.pow(corpus/totPrem,1/term)-1)*100;
    // vs Term+MF
    const termPrem=Math.round(10000000/1000*8); // rough ₹1Cr term
    const mfCorpus=(() => {let c=0,p=prem-termPrem/12;for(let y=0;y<term;y++){for(let m=0;m<12;m++)c=(c+p)*(1+eqR/100/12);}return c;})();
    return {corpus,totPrem,totCharges,cagr,mfCorpus};
  },[prem,term,eqPct,eqR,debtR,charges]);
  return(<div>
    <Sl label="Annual Premium" min={10000} max={1000000} step={10000} value={prem} onChange={setPrem} pre="₹"/>
    <Sl label="Policy Term" min={5} max={30} value={term} onChange={setTerm} suf=" yrs"/>
    <Sl label="Equity Allocation" min={0} max={100} step={5} value={eqPct} onChange={setEqPct} suf="%"/>
    <Sl label="Expected Equity Return" min={6} max={20} step={.5} value={eqR} onChange={setEqR} suf="%"/>
    <Sl label="Expected Debt Return" min={4} max={10} step={.5} value={debtR} onChange={setDebtR} suf="%"/>
    <Sl label="Total Charges (FMC+Mortality)" min={1} max={5} step={.1} value={charges} onChange={setCharges} suf="% pa"/>
    <Div/>
    <RRow label="Total Premiums Paid" value={fmt(res.totPrem)}/><RRow label="Est. Charges Deducted" value={fmt(res.totCharges)} color={T.danger}/><RRow label="ULIP Maturity Value" value={fmt(res.corpus)} hi/><RRow label="Effective CAGR" value={pct(res.cagr)}/>
    <Sec icon="⚖️" label="vs Term + Mutual Fund" color={T.secondary}/>
    <RRow label="MF Corpus (same premium)" value={fmt(res.mfCorpus)} hi color={T.secondary}/>
    {res.mfCorpus>res.corpus&&<InfoBox text={`Term+MF gives ${fmt(res.mfCorpus-res.corpus)} more than ULIP!`} color={T.secondary}/>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 4: TAX
// ═══════════════════════════════════════════════════════════════════════════════
function IncomeTaxCalc() {
  const [income,setIncome]=useState(1500000); const [regime,setRegime]=useState("new");
  const [age,setAge]=useState("below60");
  const [d80c,setD80c]=useState(150000); const [d80d,setD80d]=useState(25000);
  const [hra,setHra]=useState(0); const [hl,setHl]=useState(0);
  const calcTax=(ti,slabs)=>{let t=0,prev=0;for(const[lim,r]of slabs){if(ti<=prev)break;t+=(Math.min(ti,lim)-prev)*r;prev=lim;}return t;};
  const res=useMemo(()=>{
    const newSlabs=[[300000,0],[700000,.05],[1000000,.1],[1200000,.15],[1500000,.2],[Infinity,.3]];
    const oldSlabs=age==="above80"?[[500000,0],[1000000,.2],[Infinity,.3]]:age==="60to80"?[[300000,0],[500000,.05],[1000000,.2],[Infinity,.3]]:[[250000,0],[500000,.05],[1000000,.2],[Infinity,.3]];
    let taxable=income,deductions=0;
    if(regime==="old"){deductions=Math.min(d80c,150000)+Math.min(d80d,100000)+Math.min(hra,1000000)+Math.min(hl,200000)+50000;taxable=Math.max(0,income-deductions);}
    let tax=calcTax(taxable,regime==="new"?newSlabs:oldSlabs);
    if(regime==="new"&&taxable<=700000)tax=0;
    if(regime==="old"&&taxable<=500000)tax=0;
    const cess=tax*.04,total=tax+cess;
    const newTax=(()=>{let t=calcTax(income,newSlabs);if(income<=700000)t=0;return t*1.04;})();
    const oldD=Math.min(d80c,150000)+Math.min(d80d,100000)+Math.min(hra,1000000)+Math.min(hl,200000)+50000;
    const oldTax=(()=>{const ti=Math.max(0,income-oldD);let t=calcTax(ti,oldSlabs);if(ti<=500000)t=0;return t*1.04;})();
    return {taxable,deductions,tax,cess,total,takeHome:income-total,newTax,oldTax};
  },[income,regime,age,d80c,d80d,hra,hl]);
  return(<div>
    <Sl label="Annual Income" min={0} max={10000000} step={50000} value={income} onChange={setIncome} pre="₹"/>
    <Seg value={regime} onChange={setRegime} options={[{v:"new",l:"New Regime"},{v:"old",l:"Old Regime"}]}/>
    <Seg value={age} onChange={setAge} options={[{v:"below60",l:"< 60"},{v:"60to80",l:"60-80"},{v:"above80",l:"> 80"}]}/>
    {regime==="old"&&<div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:8,padding:12,marginBottom:12}}>
      <div style={{fontSize:10,color:T.sub,fontWeight:700,marginBottom:8}}>DEDUCTIONS</div>
      <Sl label="80C (ELSS, PPF, LIC)" min={0} max={150000} step={5000} value={d80c} onChange={setD80c} pre="₹"/>
      <Sl label="80D (Health Insurance)" min={0} max={100000} step={1000} value={d80d} onChange={setD80d} pre="₹"/>
      <Sl label="HRA" min={0} max={1000000} step={10000} value={hra} onChange={setHra} pre="₹"/>
      <Sl label="Home Loan Interest (Sec 24b)" min={0} max={200000} step={5000} value={hl} onChange={setHl} pre="₹"/>
    </div>}
    <Div/>
    <RRow label="Gross Income" value={fmt(income)}/>{regime==="old"&&<RRow label="Total Deductions" value={fmt(res.deductions)}/>}
    <RRow label="Taxable Income" value={fmt(res.taxable)}/><RRow label="Income Tax" value={fmt(res.tax)}/><RRow label="Cess (4%)" value={fmt(res.cess)}/><RRow label="Total Tax" value={fmt(res.total)} hi color={T.danger}/><RRow label="Net Take-Home" value={fmt(res.takeHome)} hi/>
    <Div/>
    <div style={{fontSize:10,color:T.sub,marginBottom:8,fontWeight:700}}>REGIME COMPARISON</div>
    <ResponsiveContainer width="100%" height={120}>
      <BarChart data={[{name:"New",Tax:Math.round(res.newTax)},{name:"Old",Tax:Math.round(res.oldTax)}]}>
        <XAxis dataKey="name" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/><Bar dataKey="Tax" radius={[4,4,0,0]}>{[0,1].map(i=><Cell key={i} fill={PALETTE[i]}/>)}</Bar>
      </BarChart>
    </ResponsiveContainer>
    {res.newTax<res.oldTax?<InfoBox text="✅ New Regime saves more tax for your income level." color={T.secondary}/>:<InfoBox text="✅ Old Regime saves more tax with your deductions." color={T.secondary}/>}
  </div>);
}

function CapGainsTaxCalc() {
  const [type,setType]=useState("equity"); const [buy,setBuy]=useState(100000); const [sell,setSell]=useState(200000);
  const [holding,setHolding]=useState(18); const [qty,setQty]=useState(100); const [slab,setSlab]=useState(30);
  const res=useMemo(()=>{
    const gain=(sell-buy)*qty;
    let tax=0,taxType="";
    if(type==="equity"){
      if(holding>=12){tax=Math.max(0,gain-125000)*0.125;taxType="LTCG 12.5% (above ₹1.25L exempt)";}
      else{tax=gain*0.20;taxType="STCG 20%";}
    } else if(type==="debt"){
      if(holding>=36){tax=gain*0.20;taxType="LTCG 20%";}// with indexation benefit
      else{tax=gain*slab/100;taxType=`STCG ${slab}% (as per slab)`;}
    } else {
      if(holding>=24){tax=gain*0.20;taxType="LTCG 20% with indexation";}
      else{tax=gain*slab/100;taxType=`STCG ${slab}% (as per slab)`;}
    }
    return {gain,tax,netProceeds:(sell-buy)*qty-tax+(buy*qty),taxType};
  },[type,buy,sell,holding,qty,slab]);
  return(<div>
    <Seg value={type} onChange={setType} options={[{v:"equity",l:"Equity"},{v:"debt",l:"Debt MF"},{v:"property",l:"Property"}]}/>
    <Sl label="Buy Price" min={1} max={100000} step={1} value={buy} onChange={setBuy} pre="₹"/>
    <Sl label="Sell Price" min={1} max={100000} step={1} value={sell} onChange={setSell} pre="₹"/>
    <Sl label="Quantity" min={1} max={10000} value={qty} onChange={setQty}/>
    <Sl label="Holding Period" min={1} max={120} value={holding} onChange={setHolding} suf=" months"/>
    {(holding<12||type==="debt"||type==="property")&&<Sel label="Income Tax Slab" value={slab} onChange={v=>setSlab(parseFloat(v))} options={[{v:"5",l:"5%"},{v:"20",l:"20%"},{v:"30",l:"30%"}]}/>}
    <Div/>
    <RRow label="Capital Gain" value={fmt(res.gain)} color={res.gain>0?T.secondary:T.danger}/><RRow label="Tax Type" value={res.taxType}/><RRow label="Tax Payable" value={fmt(res.tax)} hi color={T.danger}/><RRow label="Net Proceeds After Tax" value={fmt(res.netProceeds)} hi/>
  </div>);
}

function HRACalc() {
  const [basic,setBasic]=useState(50000); const [hra,setHra]=useState(25000);
  const [rent,setRent]=useState(20000); const [metro,setMetro]=useState(true);
  const res=useMemo(()=>{
    const e1=hra*12, e2=(rent-basic*.1)*12, e3=basic*(metro?.5:.4)*12;
    const exempt=Math.max(0,Math.min(e1,e2,e3));
    const taxable=hra*12-exempt;
    const saved=taxable*0-exempt*.3; // rough at 30%
    return {e1,e2:Math.max(0,e2),e3,exempt,taxable,saved:exempt*.3};
  },[basic,hra,rent,metro]);
  return(<div>
    <Sl label="Basic Salary (Monthly)" min={10000} max={500000} step={1000} value={basic} onChange={setBasic} pre="₹"/>
    <Sl label="HRA Received (Monthly)" min={0} max={300000} step={1000} value={hra} onChange={setHra} pre="₹"/>
    <Sl label="Rent Paid (Monthly)" min={0} max={300000} step={1000} value={rent} onChange={setRent} pre="₹"/>
    <Toggle label="Metro City" value={metro} onChange={setMetro}/>
    <Div/>
    <div style={{background:T.card2,borderRadius:8,padding:12,marginBottom:12}}>
      <div style={{fontSize:10,color:T.sub,fontWeight:700,marginBottom:8}}>HRA EXEMPTION = MIN OF:</div>
      <RRow label="1) Actual HRA (annual)" value={fmt(res.e1)}/><RRow label="2) Rent - 10% of Basic" value={fmt(res.e2)}/><RRow label={`3) ${metro?"50%":"40%"} of Basic (${metro?"Metro":"Non-Metro"})`} value={fmt(res.e3)}/>
    </div>
    <RRow label="HRA Exemption (Annual)" value={fmt(res.exempt)} hi color={T.secondary}/><RRow label="Taxable HRA" value={fmt(res.taxable)} color={T.danger}/><RRow label="Tax Saved @ 30%" value={fmt(res.saved)} color={T.purple}/>
  </div>);
}

function D80CCalc() {
  const ITEMS=[{k:"epf",l:"EPF Contribution"},{k:"ppf",l:"PPF Investment"},{k:"elss",l:"ELSS Mutual Funds"},{k:"lic",l:"LIC Premium"},{k:"tuition",l:"Tuition Fees"},{k:"hlprin",l:"Home Loan Principal"},{k:"nsc",l:"NSC"},{k:"tfd",l:"Tax-saving FD"},{k:"ssy",l:"Sukanya Samriddhi"}];
  const [vals,setVals]=useState({epf:72000,ppf:50000,elss:30000,lic:0,tuition:0,hlprin:0,nsc:0,tfd:0,ssy:0});
  const [slab,setSlab]=useState(30);
  const total=Object.values(vals).reduce((s,v)=>s+v,0);
  const effective=Math.min(total,150000);
  const saved=effective*slab/100;
  const shortfall=Math.max(0,150000-total);
  return(<div>
    {ITEMS.map(it=>(
      <div key={it.k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${T.border}22`}}>
        <span style={{fontSize:12,color:T.sub}}>{it.l}</span>
        <input type="number" value={vals[it.k]} onChange={e=>setVals({...vals,[it.k]:parseFloat(e.target.value)||0})}
          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:12,padding:"3px 7px",width:90,textAlign:"right",outline:"none"}}/>
      </div>
    ))}
    <Sel label="Tax Bracket" value={slab} onChange={v=>setSlab(parseFloat(v))} options={[{v:"5",l:"5%"},{v:"20",l:"20%"},{v:"30",l:"30%"}]}/>
    <Div/>
    <RRow label="Total 80C Investments" value={fmt(total)}/><RRow label="Allowed Deduction (Max ₹1.5L)" value={fmt(effective)} hi/><RRow label="Tax Saved" value={fmt(saved)} hi color={T.purple}/>
    {shortfall>0?<InfoBox text={`₹${Math.round(shortfall).toLocaleString("en-IN")} shortfall to max 80C. Invest more to save ${fmt(shortfall*slab/100)} more in tax.`} color={T.accent}/>:<InfoBox text="✅ You've maximized 80C deduction!" color={T.secondary}/>}
  </div>);
}

function GSTCalc() {
  const [amount,setAmount]=useState(10000); const [rate,setRate]=useState(18); const [mode,setMode]=useState("excl");
  const res=useMemo(()=>{
    if(mode==="excl"){const gst=amount*rate/100;return {base:amount,gst,total:amount+gst,cgst:gst/2,sgst:gst/2};}
    else{const base=amount*100/(100+rate);const gst=amount-base;return {base,gst,total:amount,cgst:gst/2,sgst:gst/2};}
  },[amount,rate,mode]);
  return(<div>
    <Sl label="Amount" min={1} max={10000000} step={100} value={amount} onChange={setAmount} pre="₹"/>
    <Seg value={rate.toString()} onChange={v=>setRate(parseFloat(v))} options={[{v:"5",l:"5%"},{v:"12",l:"12%"},{v:"18",l:"18%"},{v:"28",l:"28%"}]}/>
    <Seg value={mode} onChange={setMode} options={[{v:"excl",l:"Add GST"},{v:"incl",l:"Remove GST"}]}/>
    <Div/>
    <RRow label="Base Amount" value={fmt(res.base)}/><RRow label={`GST (${rate}%)`} value={fmt(res.gst)}/><RRow label="CGST" value={fmt(res.cgst)}/><RRow label="SGST" value={fmt(res.sgst)}/><RRow label="Total Amount" value={fmt(res.total)} hi/>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 5: RETIREMENT & INFLATION
// ═══════════════════════════════════════════════════════════════════════════════
function RetirementCorpusCalc() {
  const [curAge,setCurAge]=useState(30); const [retAge,setRetAge]=useState(60);
  const [exp,setExp]=useState(50000); const [inf,setInf]=useState(6); const [life,setLife]=useState(85);
  const [postR,setPostR]=useState(8);
  const res=useMemo(()=>{
    const yrs=retAge-curAge;
    const futureExp=exp*Math.pow(1+inf/100,yrs)*12; // annual at retirement
    const retYrs=life-retAge;
    const mr=postR/100/12,months=retYrs*12;
    const corpus=futureExp/12*(Math.pow(1+mr,months)-1)/(mr*Math.pow(1+mr,months-1))*Math.pow(1+mr,months);
    const corpusBuf=corpus*1.2;
    const sipNeeded=(()=>{const m=R=>R/100/12,n=yrs*12;const mr2=12/100/12;return corpus*mr2/((Math.pow(1+mr2,n)-1)*(1+mr2));})();
    return {futureExp,corpus,corpusBuf,sipNeeded,retYrs};
  },[curAge,retAge,exp,inf,life,postR]);
  return(<div>
    <Sl label="Current Age" min={20} max={55} value={curAge} onChange={setCurAge} suf=" yrs"/>
    <Sl label="Retirement Age" min={curAge+1} max={70} value={retAge} onChange={setRetAge} suf=" yrs"/>
    <Sl label="Current Monthly Expenses" min={10000} max={500000} step={5000} value={exp} onChange={setExp} pre="₹"/>
    <Sl label="Inflation Rate" min={3} max={12} step={.5} value={inf} onChange={setInf} suf="%"/>
    <Sl label="Life Expectancy" min={70} max={100} value={life} onChange={setLife} suf=" yrs"/>
    <Sl label="Post-Retirement Return" min={5} max={12} step={.5} value={postR} onChange={setPostR} suf="%"/>
    <Div/>
    <RRow label="Monthly Expenses at Retirement" value={fmt(res.futureExp/12)}/><RRow label="Retirement Duration" value={`${res.retYrs} years`}/><RRow label="Corpus Required" value={fmt(res.corpus)} hi/><RRow label="With 20% Buffer" value={fmt(res.corpusBuf)}/><RRow label="Monthly SIP Needed (12% return)" value={fmt(res.sipNeeded)} hi color={T.secondary}/>
  </div>);
}

function FIRECalc() {
  const [curAge,setCurAge]=useState(30); const [exp,setExp]=useState(1200000);
  const [savings,setSavings]=useState(2000000); const [monthly,setMonthly]=useState(50000);
  const [R,setR]=useState(12); const [swr,setSwr]=useState(4);
  const res=useMemo(()=>{
    const fire=exp*100/swr;
    const gap=Math.max(0,fire-savings);
    const mr=R/100/12;
    // Newton's method to find months to reach gap
    let months=0,bal=savings;
    while(bal<fire&&months<600){bal=bal*(1+mr)+monthly;months++;}
    return {fire,gap,months,fireAge:curAge+months/12,savingsRate:(monthly*12/exp)*100};
  },[curAge,exp,savings,monthly,R,swr]);
  return(<div>
    <Sl label="Current Age" min={20} max={50} value={curAge} onChange={setCurAge} suf=" yrs"/>
    <Sl label="Annual Expenses" min={300000} max={5000000} step={50000} value={exp} onChange={setExp} pre="₹"/>
    <Sl label="Current Savings/Investments" min={0} max={50000000} step={100000} value={savings} onChange={setSavings} pre="₹"/>
    <Sl label="Monthly Savings" min={5000} max={500000} step={5000} value={monthly} onChange={setMonthly} pre="₹"/>
    <Sl label="Expected Return" min={8} max={18} step={.5} value={R} onChange={setR} suf="%"/>
    <Sl label="Safe Withdrawal Rate" min={2} max={6} step={.5} value={swr} onChange={setSwr} suf="%"/>
    <Div/>
    <RRow label="FIRE Number" value={fmt(res.fire)} hi/><RRow label="Gap to Fill" value={fmt(Math.max(0,res.gap))}/><RRow label="Years to FIRE" value={`${(res.months/12).toFixed(1)} years`}/><RRow label="FIRE Age" value={`${res.fireAge.toFixed(1)} years`} hi color={T.secondary}/><RRow label="Savings Rate" value={pct(res.savingsRate)}/>
    <InfoBox text={`At ${swr}% withdrawal: can withdraw ${fmt(exp/12)}/month forever if corpus ≥ ${fmt(res.fire)}`}/>
  </div>);
}

function RealReturnCalc() {
  const [nominal,setNominal]=useState(12); const [inf,setInf]=useState(6); const [Y,setY]=useState(10); const [amt,setAmt]=useState(100000);
  const res=useMemo(()=>{
    const real=((1+nominal/100)/(1+inf/100)-1)*100;
    const nomFV=amt*Math.pow(1+nominal/100,Y);
    const realFV=amt*Math.pow(1+real/100,Y);
    const ld=Array.from({length:Y},(_,i)=>({year:`Y${i+1}`,Nominal:Math.round(amt*Math.pow(1+nominal/100,i+1)),Real:Math.round(amt*Math.pow(1+real/100,i+1))}));
    return {real,nomFV,realFV,ld};
  },[nominal,inf,Y,amt]);
  return(<div>
    <Sl label="Nominal Return" min={1} max={30} step={.5} value={nominal} onChange={setNominal} suf="%"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.5} value={inf} onChange={setInf} suf="%"/>
    <Sl label="Investment Amount" min={10000} max={10000000} step={10000} value={amt} onChange={setAmt} pre="₹"/>
    <Sl label="Period" min={1} max={40} value={Y} onChange={setY} suf=" yrs"/>
    <Div/>
    <RRow label="Nominal Return" value={pct(nominal)}/><RRow label="Real Return (after inflation)" value={pct(res.real)} hi/><RRow label="Nominal Value" value={fmt(res.nomFV)}/><RRow label="Real Value (Today's Purchasing Power)" value={fmt(res.realFV)} hi color={T.secondary}/>
    <Div/>
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={res.ld}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false} interval={Math.floor(Y/5)}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Nominal" stroke={PALETTE[0]} dot={false} strokeWidth={2}/>
        <Line type="monotone" dataKey="Real" stroke={PALETTE[1]} dot={false} strokeWidth={2}/>
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>
    </ResponsiveContainer>
  </div>);
}

function InflationCalc() {
  const [amt,setAmt]=useState(100000); const [R,setR]=useState(5.09); const [Y,setY]=useState(10);
  const res=useMemo(()=>{
    const fv=amt*Math.pow(1+R/100,Y), loss=((fv-amt)/amt)*100;
    const ld=Array.from({length:Y},(_,i)=>({year:`Y${i+1}`,"Future Cost":Math.round(amt*Math.pow(1+R/100,i+1)),"Purchasing Power":Math.round(amt/Math.pow(1+R/100,i+1))}));
    return {fv,loss,ld};
  },[amt,R,Y]);
  return(<div>
    <Sl label="Current Amount" min={1000} max={10000000} step={1000} value={amt} onChange={setAmt} pre="₹"/>
    <Sl label="Inflation Rate" min={1} max={20} step={.01} value={R} onChange={setR} suf="%"/>
    <Sl label="Time Period" min={1} max={50} value={Y} onChange={setY} suf=" yrs"/>
    <Div/>
    <RRow label="Future Cost" value={fmt(res.fv)} hi/><RRow label="Purchasing Power Loss" value={pct(res.loss)} color={T.danger}/>
    <Div/>
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={res.ld}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false} interval={Math.floor(Y/5)}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Future Cost" stroke={T.danger} dot={false} strokeWidth={2}/>
        <Line type="monotone" dataKey="Purchasing Power" stroke={T.secondary} dot={false} strokeWidth={2}/>
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>
    </ResponsiveContainer>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 6: REAL ESTATE
// ═══════════════════════════════════════════════════════════════════════════════
function RentVsBuyCalc() {
  const [price,setPrice]=useState(10000000); const [dp,setDp]=useState(2000000);
  const [R,setR]=useState(8.5); const [Y,setY]=useState(20); const [rent,setRent]=useState(25000);
  const [rentInc,setRentInc]=useState(5); const [propApp,setPropApp]=useState(7); const [invR,setInvR]=useState(12);
  const res=useMemo(()=>{
    const loan=price-dp, mr=R/100/12, n=Y*12;
    const emi=(loan*mr*Math.pow(1+mr,n))/(Math.pow(1+mr,n)-1);
    const totEmi=emi*n, totInt=totEmi-loan;
    const futurePrice=price*Math.pow(1+propApp/100,Y);
    const totalBuyCost=dp+totEmi;
    // Rent scenario: invest DP + (EMI-rent difference) each month
    let invBal=dp,totalRent=0,monthlyRent=rent;
    for(let y=0;y<Y;y++){
      const annRent=monthlyRent*12;totalRent+=annRent;
      const extra=Math.max(0,emi-monthlyRent)*12;
      invBal=(invBal+extra)*Math.pow(1+invR/100,1);
      monthlyRent*=1+rentInc/100;
    }
    return {emi,futurePrice,totalBuyCost,invBal,totalRent,netBuy:futurePrice-totalBuyCost,netRent:invBal-totalRent};
  },[price,dp,R,Y,rent,rentInc,propApp,invR]);
  return(<div>
    <Sl label="Property Price" min={1000000} max={50000000} step={500000} value={price} onChange={setPrice} pre="₹"/>
    <Sl label="Down Payment" min={200000} max={price} step={200000} value={dp} onChange={setDp} pre="₹"/>
    <Sl label="Loan Rate" min={7} max={15} step={.25} value={R} onChange={setR} suf="%"/>
    <Sl label="Tenure" min={5} max={30} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Current Monthly Rent" min={5000} max={200000} step={1000} value={rent} onChange={setRent} pre="₹"/>
    <Sl label="Rent Increase per Year" min={0} max={15} step={.5} value={rentInc} onChange={setRentInc} suf="%"/>
    <Sl label="Property Appreciation" min={2} max={15} step={.5} value={propApp} onChange={setPropApp} suf="%"/>
    <Sl label="Investment Return (if renting)" min={8} max={20} step={.5} value={invR} onChange={setInvR} suf="%"/>
    <Div/>
    <Sec icon="🏠" label="Buy Scenario" color={T.primary}/>
    <RRow label="Monthly EMI" value={fmt(res.emi)}/>
    <RRow label={`Property Value after ${Y} yrs`} value={fmt(res.futurePrice)}/>
    <RRow label="Total Cost (DP+EMI)" value={fmt(res.totalBuyCost)}/>
    <RRow label="Net Worth from Buying" value={fmt(res.netBuy)} hi color={res.netBuy>res.netRent?T.secondary:T.text}/>
    <Sec icon="🏢" label="Rent + Invest Scenario" color={T.accent}/>
    <RRow label="Total Rent Paid" value={fmt(res.totalRent)}/><RRow label="Investment Portfolio" value={fmt(res.invBal)}/><RRow label="Net Worth from Renting" value={fmt(res.netRent)} hi color={res.netRent>res.netBuy?T.secondary:T.text}/>
    {res.netBuy>res.netRent?
      <InfoBox text={`🏠 Buying is financially better by ${fmt(res.netBuy-res.netRent)}`} color={T.secondary}/>:
      <InfoBox text={`🏢 Renting + Investing is better by ${fmt(res.netRent-res.netBuy)}`} color={T.accent}/>
    }
  </div>);
}

function RentalYieldCalc() {
  const [propVal,setPropVal]=useState(10000000); const [rent,setRent]=useState(30000);
  const [maint,setMaint]=useState(5000); const [propTax,setPropTax]=useState(50000); const [vacMonth,setVacMonth]=useState(1);
  const res=useMemo(()=>{
    const annRent=rent*(12-vacMonth);
    const annExp=maint*12+propTax;
    const netInc=annRent-annExp;
    const grossYield=(rent*12/propVal)*100, netYield=(netInc/propVal)*100;
    const breakeven=8-netYield; // FD at 7%
    return {annRent,annExp,netInc,grossYield,netYield,breakeven};
  },[propVal,rent,maint,propTax,vacMonth]);
  return(<div>
    <Sl label="Property Value" min={500000} max={50000000} step={500000} value={propVal} onChange={setPropVal} pre="₹"/>
    <Sl label="Monthly Rent" min={5000} max={500000} step={1000} value={rent} onChange={setRent} pre="₹"/>
    <Sl label="Monthly Maintenance" min={0} max={50000} step={500} value={maint} onChange={setMaint} pre="₹"/>
    <Sl label="Annual Property Tax" min={0} max={200000} step={5000} value={propTax} onChange={setPropTax} pre="₹"/>
    <Sl label="Vacant Months per Year" min={0} max={6} value={vacMonth} onChange={setVacMonth} suf=" mo"/>
    <Div/>
    <RRow label="Annual Rental Income" value={fmt(res.annRent)}/><RRow label="Annual Expenses" value={fmt(res.annExp)} color={T.danger}/><RRow label="Net Annual Income" value={fmt(res.netInc)}/><RRow label="Gross Rental Yield" value={pct(res.grossYield)}/><RRow label="Net Rental Yield" value={pct(res.netYield)} hi/>
    {res.netYield<7&&<InfoBox text={`Net yield ${pct(res.netYield)} < FD rate 7%. Need ${pct(res.breakeven)} more appreciation to justify.`} color={T.accent}/>}
  </div>);
}

function StampDutyCalc() {
  const [state,setState]=useState("maharashtra"); const [propVal,setPropVal]=useState(10000000);
  const [type,setType]=useState("residential"); const [gender,setGender]=useState("male");
  const STATES={maharashtra:{male:5,female:4,reg:1},delhi:{male:6,female:4,reg:.5},karnataka:{male:5.6,female:5.6,reg:1},telangana:{male:4,female:4,reg:.5},tamilnadu:{male:7,female:7,reg:1}};
  const res=useMemo(()=>{
    const s=STATES[state]||STATES.maharashtra;
    const sRate=gender==="female"?s.female:s.male;
    const stamp=propVal*sRate/100, reg=propVal*s.reg/100;
    return {stamp,reg,total:stamp+reg,sRate,rRate:s.reg};
  },[state,propVal,gender]);
  return(<div>
    <Sel label="State" value={state} onChange={setState} options={[{v:"maharashtra",l:"Maharashtra"},{v:"delhi",l:"Delhi"},{v:"karnataka",l:"Karnataka"},{v:"telangana",l:"Telangana"},{v:"tamilnadu",l:"Tamil Nadu"}]}/>
    <Sl label="Property Value" min={100000} max={50000000} step={100000} value={propVal} onChange={setPropVal} pre="₹"/>
    <Seg value={gender} onChange={setGender} options={[{v:"male",l:"Male"},{v:"female",l:"Female"}]}/>
    <InfoBox text="Women buyers get lower stamp duty in Maharashtra, Delhi & several other states."/>
    <Div/>
    <RRow label={`Stamp Duty (${res.sRate}%)`} value={fmt(res.stamp)}/><RRow label={`Registration (${res.rRate}%)`} value={fmt(res.reg)}/><RRow label="Total Upfront Charges" value={fmt(res.total)} hi/>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 7: SALARY
// ═══════════════════════════════════════════════════════════════════════════════
function InHandCalc() {
  const [ctc,setCtc]=useState(1200000); const [bonus,setBonus]=useState(100000);
  const [regime,setRegime]=useState("new"); const [d80c,setD80c]=useState(150000);
  const res=useMemo(()=>{
    const basic=ctc*.5, hraAnn=ctc*.25;
    const epf=basic*.12, profTax=2400;
    const taxableIncome=regime==="new"?ctc-epf:ctc-epf-Math.min(d80c,150000)-50000;
    const calcTax=(ti)=>{
      if(regime==="new"){const slabs=[[300000,0],[700000,.05],[1000000,.1],[1200000,.15],[1500000,.2],[Infinity,.3]];let t=0,p=0;for(const[l,r]of slabs){if(ti<=p)break;t+=(Math.min(ti,l)-p)*r;p=l;}if(ti<=700000)t=0;return t*1.04;}
      else{const slabs=[[250000,0],[500000,.05],[1000000,.2],[Infinity,.3]];let t=0,p=0;for(const[l,r]of slabs){if(ti<=p)break;t+=(Math.min(ti,l)-p)*r;p=l;}if(ti<=500000)t=0;return t*1.04;}
    };
    const tax=calcTax(Math.max(0,taxableIncome));
    const takeHome=ctc-epf-profTax-tax;
    return {basic,epf,profTax,tax,takeHome,monthly:takeHome/12};
  },[ctc,bonus,regime,d80c]);
  return(<div>
    <Sl label="Annual CTC" min={300000} max={10000000} step={50000} value={ctc} onChange={setCtc} pre="₹"/>
    <Sl label="Annual Bonus (included in CTC)" min={0} max={2000000} step={50000} value={bonus} onChange={setBonus} pre="₹"/>
    <Seg value={regime} onChange={setRegime} options={[{v:"new",l:"New Regime"},{v:"old",l:"Old Regime"}]}/>
    {regime==="old"&&<Sl label="80C Investments" min={0} max={150000} step={5000} value={d80c} onChange={setD80c} pre="₹"/>}
    <Div/>
    <div style={{background:T.card2,borderRadius:8,padding:12,marginBottom:10}}>
      <div style={{fontSize:10,color:T.sub,fontWeight:700,marginBottom:6}}>SALARY BREAKDOWN</div>
      <RRow label="Basic (50%)" value={fmt(res.basic)}/><RRow label="HRA (25%)" value={fmt(ctc*.25)}/><RRow label="Allowances" value={fmt(ctc-res.basic-ctc*.25)}/>
    </div>
    <div style={{background:T.card2,borderRadius:8,padding:12,marginBottom:10}}>
      <div style={{fontSize:10,color:T.sub,fontWeight:700,marginBottom:6}}>DEDUCTIONS</div>
      <RRow label="EPF (12% of Basic)" value={fmt(res.epf)} color={T.danger}/><RRow label="Professional Tax" value={fmt(res.profTax)} color={T.danger}/><RRow label="Income Tax" value={fmt(res.tax)} color={T.danger}/>
    </div>
    <RRow label="Annual Take-Home" value={fmt(res.takeHome)} hi/><RRow label="Monthly In-Hand" value={fmt(res.monthly)} hi color={T.secondary}/>
  </div>);
}

function GratuityCalc() {
  const [basic,setBasic]=useState(60000); const [yrs,setYrs]=useState(10); const [covered,setCovered]=useState(true);
  const res=useMemo(()=>{
    const gratuity=covered?(basic*yrs*15)/26:(basic*yrs*15)/30;
    const maxExempt=2000000;
    const taxable=Math.max(0,gratuity-maxExempt);
    return {gratuity:Math.min(gratuity,covered?2000000:Infinity),taxable};
  },[basic,yrs,covered]);
  return(<div>
    <Sl label="Last Drawn Basic Salary" min={10000} max={500000} step={1000} value={basic} onChange={setBasic} pre="₹"/>
    <Sl label="Years of Service (min 5)" min={5} max={40} value={yrs} onChange={setYrs} suf=" yrs"/>
    <Toggle label="Covered under Gratuity Act" value={covered} onChange={setCovered}/>
    <InfoBox text={covered?"Formula: (Basic × Years × 15) / 26. Max ₹20L tax-free.":"Formula: (Basic × Years × 15) / 30. No statutory limit."}/>
    <Div/>
    <RRow label="Gratuity Amount" value={fmt(res.gratuity)} hi/><RRow label="Tax Exempt (Max ₹20L)" value={fmt(Math.min(res.gratuity,2000000))} color={T.secondary}/><RRow label="Taxable Amount" value={fmt(res.taxable)} color={res.taxable>0?T.danger:T.sub}/>
  </div>);
}

function SalaryHikeCalc() {
  const [cur,setCur]=useState(1200000); const [hike,setHike]=useState(15); const [yrs,setYrs]=useState(5);
  const newCtc=cur*(1+hike/100);
  const projection=Array.from({length:yrs},(_,i)=>({year:`Y${i+1}`,CTC:Math.round(cur*Math.pow(1+hike/100,i+1))}));
  return(<div>
    <Sl label="Current CTC" min={300000} max={10000000} step={50000} value={cur} onChange={setCur} pre="₹"/>
    <Sl label="Hike Percentage" min={1} max={100} step={.5} value={hike} onChange={setHike} suf="%"/>
    <Sl label="Projection Years" min={1} max={20} value={yrs} onChange={setYrs} suf=" yrs"/>
    <Div/>
    <RRow label="New CTC" value={fmt(newCtc)} hi/><RRow label="Annual Increment" value={fmt(newCtc-cur)} color={T.secondary}/><RRow label="Monthly Increase (Gross)" value={fmt((newCtc-cur)/12)}/>
    <Div/>
    <ResponsiveContainer width="100%" height={150}>
      <BarChart data={projection}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/><Bar dataKey="CTC" fill={PALETTE[0]} radius={[4,4,0,0]}/>
      </BarChart>
    </ResponsiveContainer>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 8: TRADING & CRYPTO
// ═══════════════════════════════════════════════════════════════════════════════
function BrokerageCalc() {
  const [val,setVal]=useState(100000); const [type,setType]=useState("delivery");
  const brokers=[
    {name:"Zerodha",delivery:0,intraday:.03,stt:{delivery:.1,intraday:.025},
      calc:(v,t)=>{const b=t==="delivery"?0:Math.min(v*.0003,20);const stt=t==="delivery"?v*.001:v*.00025;const exc=v*.0000325;const gst=(b+exc)*.18;const sebi=v*.000001;const stamp=t==="delivery"?v*.00015:v*.00003;return {b,stt,exc,gst,sebi,stamp,tot:b+stt+exc+gst+sebi+stamp};}},
    {name:"Groww",delivery:0,intraday:.05,
      calc:(v,t)=>{const b=t==="delivery"?0:Math.min(v*.0005,20);const stt=t==="delivery"?v*.001:v*.00025;const exc=v*.0000325;const gst=(b+exc)*.18;const sebi=v*.000001;const stamp=t==="delivery"?v*.00015:v*.00003;return {b,stt,exc,gst,sebi,stamp,tot:b+stt+exc+gst+sebi+stamp};}},
    {name:"ICICI Direct",delivery:.5,intraday:.275,
      calc:(v,t)=>{const b=t==="delivery"?v*.005:v*.00275;const stt=t==="delivery"?v*.001:v*.00025;const exc=v*.0000325;const gst=(b+exc)*.18;const sebi=v*.000001;const stamp=t==="delivery"?v*.00015:v*.00003;return {b,stt,exc,gst,sebi,stamp,tot:b+stt+exc+gst+sebi+stamp};}},
  ];
  const results=brokers.map(br=>({...br,...br.calc(val,type)}));
  const cheapest=Math.min(...results.map(r=>r.tot));
  return(<div>
    <Sl label="Trade Value" min={1000} max={10000000} step={1000} value={val} onChange={setVal} pre="₹"/>
    <Seg value={type} onChange={setType} options={[{v:"delivery",l:"Delivery"},{v:"intraday",l:"Intraday"}]}/>
    <Div/>
    {results.map(r=>(
      <div key={r.name} style={{background:r.tot===cheapest?T.secondary+"11":T.card2,border:`1px solid ${r.tot===cheapest?T.secondary:T.border}`,borderRadius:8,padding:"10px 12px",marginBottom:8}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>{r.name}</span>
          <span style={{fontSize:14,fontWeight:800,color:r.tot===cheapest?T.secondary:T.text}}>₹{r.tot.toFixed(2)}</span>
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {[["Brokerage",r.b],["STT",r.stt],["Exchange",r.exc],["GST",r.gst]].map(([l,v])=>(
            <span key={l} style={{fontSize:10,color:T.muted}}>{l}: ₹{v.toFixed(2)}</span>
          ))}
        </div>
        {r.tot===cheapest&&<div style={{fontSize:10,color:T.secondary,marginTop:4,fontWeight:700}}>✅ Cheapest</div>}
      </div>
    ))}
    <InfoBox text={`Savings with cheapest vs ICICI: ₹${(results[2]?.tot-cheapest).toFixed(2)}`} color={T.secondary}/>
  </div>);
}

function PositionSizingCalc() {
  const [capital,setCapital]=useState(500000); const [riskPct,setRiskPct]=useState(2);
  const [entry,setEntry]=useState(100); const [sl,setSL]=useState(95);
  const res=useMemo(()=>{
    const riskAmt=capital*riskPct/100, riskPerShare=entry-sl;
    const shares=riskPerShare>0?Math.floor(riskAmt/riskPerShare):0;
    const totInv=shares*entry, slLoss=shares*riskPerShare;
    const targets=[{r:"1:1",p:entry+(entry-sl)},{r:"1:2",p:entry+(entry-sl)*2},{r:"1:3",p:entry+(entry-sl)*3}];
    return {riskAmt,riskPerShare,shares,totInv,slLoss,targets};
  },[capital,riskPct,entry,sl]);
  return(<div>
    <Sl label="Trading Capital" min={10000} max={10000000} step={10000} value={capital} onChange={setCapital} pre="₹"/>
    <Sl label="Risk per Trade" min={.5} max={10} step={.5} value={riskPct} onChange={setRiskPct} suf="%"/>
    <Sl label="Entry Price" min={1} max={100000} step={.5} value={entry} onChange={setEntry} pre="₹"/>
    <Sl label="Stop Loss Price" min={1} max={entry} step={.5} value={sl} onChange={setSL} pre="₹"/>
    <Div/>
    <RRow label="Risk Amount" value={fmt(res.riskAmt)}/><RRow label="Shares to Buy" value={num(res.shares)} hi/><RRow label="Total Investment" value={fmt(res.totInv)}/><RRow label="Max Loss (if SL hit)" value={fmt(res.slLoss)} color={T.danger}/>
    <Sec icon="🎯" label="Reward Targets"/>
    {res.targets.map(t=><RRow key={t.r} label={`R:R ${t.r} → ₹${t.p.toFixed(2)}`} value={fmt(res.shares*(t.p-entry))} color={T.secondary}/>)}
  </div>);
}

function CryptoTaxCalc() {
  const [gain,setGain]=useState(500000); const [tds,setTds]=useState(0);
  const res=useMemo(()=>{
    const tax=gain*.3, net=gain-tax-tds;
    return {tax,net,tds};
  },[gain,tds]);
  return(<div>
    <InfoBox text="India VDA Tax (2022+): Flat 30% on all crypto gains. No deductions allowed except cost of acquisition. 1% TDS on sell transactions > ₹10,000."/>
    <Sl label="Total Crypto Gains" min={0} max={10000000} step={10000} value={gain} onChange={setGain} pre="₹"/>
    <Sl label="TDS Already Deducted" min={0} max={500000} step={1000} value={tds} onChange={setTds} pre="₹"/>
    <Div/>
    <RRow label="Gross Gain" value={fmt(gain)}/><RRow label="Tax @ 30%" value={fmt(res.tax)} hi color={T.danger}/><RRow label="TDS Already Paid" value={fmt(tds)}/><RRow label="Net Tax Payable" value={fmt(Math.max(0,res.tax-tds))} color={T.danger}/><RRow label="Net Gain After Tax" value={fmt(res.net)} hi/>
    <InfoBox text="Crypto losses CANNOT be set off against other income or carried forward." color={T.danger}/>
  </div>);
}

function OptionsCalc() {
  const [strike,setStrike]=useState(20000); const [prem,setPrem]=useState(150);
  const [spot,setSpot]=useState(20300); const [lots,setLots]=useState(1); const [type,setType]=useState("call");
  const lotSize=25; // Nifty
  const res=useMemo(()=>{
    const qty=lots*lotSize, cost=prem*qty;
    let pnl=0;
    if(type==="call") pnl=Math.max(0,spot-strike)*qty-cost;
    else pnl=Math.max(0,strike-spot)*qty-cost;
    const be=type==="call"?strike+prem:strike-prem;
    const ld=Array.from({length:21},(_,i)=>{
      const s=spot-1000+i*100;
      const p=type==="call"?Math.max(0,s-strike)*qty-cost:Math.max(0,strike-s)*qty-cost;
      return {spot:s,PnL:Math.round(p)};
    });
    return {pnl,be,cost,ld};
  },[strike,prem,spot,lots,type]);
  return(<div>
    <Seg value={type} onChange={setType} options={[{v:"call",l:"Call Option"},{v:"put",l:"Put Option"}]}/>
    <Sl label="Strike Price" min={15000} max={30000} step={50} value={strike} onChange={setStrike} pre="₹"/>
    <Sl label="Premium Paid" min={10} max={2000} step={10} value={prem} onChange={setPrem} pre="₹"/>
    <Sl label="Spot Price at Expiry" min={15000} max={30000} step={50} value={spot} onChange={setSpot} pre="₹"/>
    <Sl label="Number of Lots" min={1} max={50} value={lots} onChange={setLots}/>
    <Div/>
    <RRow label="Premium Paid (Cost)" value={fmt(res.cost)} color={T.danger}/><RRow label="Breakeven" value={`₹${res.be}`}/><RRow label="P&L at Current Spot" value={fmt(res.pnl)} hi color={res.pnl>=0?T.secondary:T.danger}/>
    <Div/>
    <div style={{fontSize:10,color:T.sub,marginBottom:6}}>PAYOFF DIAGRAM</div>
    <ResponsiveContainer width="100%" height={160}>
      <ComposedChart data={res.ld}>
        <XAxis dataKey="spot" tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} tickFormatter={v=>v.toLocaleString("en-IN")}/>
        <YAxis tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={50} tickFormatter={v=>v>=0?"₹"+Math.round(v/1000)+"k":"-₹"+Math.round(-v/1000)+"k"}/>
        <Tooltip content={<CTip/>}/>
        <ReferenceLine y={0} stroke={T.border} strokeDasharray="4 2"/>
        <Area type="monotone" dataKey="PnL" stroke={T.secondary} fill={T.secondary+"22"} strokeWidth={2}/>
      </ComposedChart>
    </ResponsiveContainer>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 9: GOLD
// ═══════════════════════════════════════════════════════════════════════════════
function GoldCalc() {
  const [inv,setInv]=useState(100000); const [goldRate,setGoldRate]=useState(6500); const [app,setApp]=useState(8); const [Y,setY]=useState(10);
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const res=useMemo(()=>{
    const grams=inv/goldRate;
    const futureRate=goldRate*Math.pow(1+app/100,Y);
    const futureVal=grams*futureRate;
    const ret=(futureVal-inv)/inv*100;
    const inflAdj=futureVal/Math.pow(1+inf/100,Y);
    return {grams,futureRate,futureVal,ret,inflAdj};
  },[inv,goldRate,app,Y,inf]);
  return(<div>
    <Sl label="Investment Amount" min={10000} max={10000000} step={10000} value={inv} onChange={setInv} pre="₹"/>
    <Sl label="Current Gold Rate (per gram)" min={4000} max={10000} step={100} value={goldRate} onChange={setGoldRate} pre="₹"/>
    <Sl label="Expected Appreciation" min={3} max={20} step={.5} value={app} onChange={setApp} suf="%"/>
    <Sl label="Investment Period" min={1} max={30} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <Div/>
    <RRow label="Gold Purchased" value={`${res.grams.toFixed(2)} grams`}/>
    <RRow label={`Gold Rate After ${Y} yrs`} value={fmt(res.futureRate)+"/g"}/>
    <RRow label="Portfolio Value" value={fmt(res.futureVal)} hi/>
    <RRow label="Returns" value={pct(res.ret)}/>
    <RRow label="CAGR" value={pct(app)}/>
    {showInf&&<RRow label="Inflation-Adjusted Value" value={fmt(res.inflAdj)} hi color="#ff8a65"/>}
  </div>);
}

function SGBCalc() {
  const [inv,setInv]=useState(50000); const [issue,setIssue]=useState(6500); const [goldApp,setGoldApp]=useState(8);
  const res=useMemo(()=>{
    const grams=inv/issue, intR=0.025, Y=8;
    const futureGold=issue*Math.pow(1+goldApp/100,Y);
    const maturity=grams*futureGold;
    const intEarned=inv*intR*Y;
    const totVal=maturity+intEarned;
    return {grams,maturity,intEarned,totVal,annInt:inv*intR};
  },[inv,issue,goldApp]);
  return(<div>
    <Sl label="Investment Amount" min={10000} max={5000000} step={5000} value={inv} onChange={setInv} pre="₹"/>
    <Sl label="Issue Price (per gram)" min={4000} max={10000} step={100} value={issue} onChange={setIssue} pre="₹"/>
    <Sl label="Gold Appreciation (8 yr)" min={3} max={15} step={.5} value={goldApp} onChange={setGoldApp} suf="%"/>
    <InfoBox text="SGB: 2.5% p.a. interest + capital gains. Tax-free on maturity. 8-year lock-in."/>
    <Div/>
    <RRow label="Gold Bonds (Grams)" value={`${res.grams.toFixed(2)}g`}/><RRow label="Annual Interest" value={fmt(res.annInt)} color={T.secondary}/><RRow label="Total Interest (8 yrs)" value={fmt(res.intEarned)}/><RRow label="Maturity Value (Gold)" value={fmt(res.maturity)}/><RRow label="Total Value" value={fmt(res.totVal)} hi/><RRow label="Tax on Maturity" value="₹0 (Tax-free)" color={T.secondary}/>
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY 10: MISC
// ═══════════════════════════════════════════════════════════════════════════════
function EmergencyFundCalc() {
  const [exp,setExp]=useState(50000); const [months,setMonths]=useState(6); const [savings,setSavings]=useState(0);
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const res=useMemo(()=>{
    const req=exp*months, short=Math.max(0,req-savings);
    const inflReq=req*Math.pow(1+inf/100,1);
    return {req,short,monthly:short/12,inflReq};
  },[exp,months,savings,inf]);
  return(<div>
    <Sl label="Monthly Expenses" min={10000} max={1000000} step={5000} value={exp} onChange={setExp} pre="₹"/>
    <Sl label="Months to Cover" min={3} max={24} value={months} onChange={setMonths} suf=" mo"/>
    <Sl label="Current Savings" min={0} max={10000000} step={10000} value={savings} onChange={setSavings} pre="₹"/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted target" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <Div/>
    <RRow label="Required Fund" value={fmt(res.req)} hi/>{showInf&&<RRow label="Inflation-Adjusted (1yr)" value={fmt(res.inflReq)} hi color="#ff8a65" indent/>}
    <RRow label="Shortfall" value={fmt(res.short)} color={res.short>0?T.danger:T.secondary}/><RRow label="Monthly Savings Needed" value={fmt(res.monthly)}/>
    {res.short===0&&<InfoBox text="✅ Emergency fund is fully covered!" color={T.secondary}/>}
  </div>);
}

function FIRENumberCalc() { return <FIRECalc/>; }

function Rule72Calc() {
  const [R,setR]=useState(12); const [yrs,setYrs]=useState(6);
  const fromRate=72/R; const fromYrs=72/yrs;
  return(<div>
    <div style={{textAlign:"center",padding:"16px 0",background:T.card2,borderRadius:10,marginBottom:16}}>
      <div style={{fontSize:11,color:T.sub,marginBottom:4}}>RULE OF 72</div>
      <div style={{fontSize:40,fontWeight:900,color:T.primary,fontFamily:"monospace"}}>{fromRate.toFixed(1)}</div>
      <div style={{fontSize:13,color:T.sub}}>years to double at {R}% return</div>
    </div>
    <Sl label="Return Rate" min={1} max={30} step={.5} value={R} onChange={setR} suf="%"/>
    <Div/>
    <div style={{background:T.card2,borderRadius:8,padding:12,marginBottom:12}}>
      <div style={{fontSize:10,color:T.sub,fontWeight:700,marginBottom:8}}>AT DIFFERENT RATES</div>
      {[6,8,10,12,15,18,24].map(r=>(
        <div key={r} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.border}22`}}>
          <span style={{fontSize:12,color:T.sub}}>{r}% return</span>
          <span style={{fontSize:12,color:T.text,fontWeight:700}}>{(72/r).toFixed(1)} years to double</span>
        </div>
      ))}
    </div>
    <Sl label="Desired Doubling Period" min={2} max={24} value={yrs} onChange={setYrs} suf=" yrs"/>
    <RRow label="Required Return Rate" value={pct(fromYrs)} hi color={T.secondary}/>
  </div>);
}

function DTICalc() {
  const [income,setIncome]=useState(100000);
  const [debts,setDebts]=useState([{name:"Home Loan",emi:40000},{name:"Personal Loan",emi:10000},{name:"Car Loan",emi:5000}]);
  const totDebt=debts.reduce((s,d)=>s+d.emi,0);
  const dti=(totDebt/income)*100;
  const getColor=()=>dti<36?T.secondary:dti<43?"#ffb800":dti<50?T.accent:T.danger;
  const getLabel=()=>dti<36?"Excellent":dti<43?"Good":dti<50?"Risky":"Danger Zone!";
  return(<div>
    <Sl label="Monthly Income" min={20000} max={500000} step={5000} value={income} onChange={setIncome} pre="₹"/>
    <div style={{fontSize:11,color:T.sub,fontWeight:700,marginBottom:8}}>MONTHLY EMIS</div>
    {debts.map((d,i)=>(
      <div key={i} style={{display:"flex",gap:6,marginBottom:6}}>
        <input value={d.name} onChange={e=>{const n=[...debts];n[i]={...d,name:e.target.value};setDebts(n);}} style={{flex:2,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:12,padding:"5px 7px",outline:"none"}}/>
        <input type="number" value={d.emi} onChange={e=>{const n=[...debts];n[i]={...d,emi:parseFloat(e.target.value)||0};setDebts(n);}} style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,color:T.text,fontSize:12,padding:"5px 7px",outline:"none"}}/>
        <button onClick={()=>setDebts(debts.filter((_,j)=>j!==i))} style={{background:T.danger+"22",border:`1px solid ${T.danger}44`,borderRadius:5,color:T.danger,fontSize:11,padding:"4px 7px",cursor:"pointer"}}>×</button>
      </div>
    ))}
    <button onClick={()=>setDebts([...debts,{name:"New EMI",emi:5000}])} style={{width:"100%",padding:"6px 0",borderRadius:7,border:`1px dashed ${T.border}`,background:"transparent",color:T.sub,fontSize:11,cursor:"pointer",marginBottom:10}}>+ Add EMI</button>
    <Div/>
    <div style={{textAlign:"center",padding:16,background:getColor()+"11",border:`1px solid ${getColor()}44`,borderRadius:10,marginBottom:12}}>
      <div style={{fontSize:36,fontWeight:900,color:getColor(),fontFamily:"monospace"}}>{dti.toFixed(1)}%</div>
      <div style={{fontSize:13,color:getColor(),fontWeight:700}}>DTI Ratio — {getLabel()}</div>
    </div>
    <RRow label="Total Monthly EMIs" value={fmt(totDebt)}/><RRow label="Available Income" value={fmt(income-totDebt)}/>
  </div>);
}

function CollegeCostCalc() {
  const [curAge,setCurAge]=useState(5); const [cost,setCost]=useState(2000000);
  const [eduInf,setEduInf]=useState(10); const [R,setR]=useState(12);
  const res=useMemo(()=>{
    const yrs=18-curAge, fut=cost*Math.pow(1+eduInf/100,yrs);
    const mr=R/100/12,n=yrs*12;
    const sip=fut*mr/((Math.pow(1+mr,n)-1)*(1+mr));
    const lumpsum=fut/Math.pow(1+R/100,yrs);
    return {yrs,fut,sip,lumpsum};
  },[curAge,cost,eduInf,R]);
  return(<div>
    <Sl label="Child's Current Age" min={0} max={17} value={curAge} onChange={setCurAge} suf=" yrs"/>
    <Sl label="Current College Cost" min={200000} max={10000000} step={100000} value={cost} onChange={setCost} pre="₹"/>
    <Sl label="Education Inflation" min={5} max={15} step={.5} value={eduInf} onChange={setEduInf} suf="%"/>
    <Sl label="Expected Investment Return" min={8} max={20} step={.5} value={R} onChange={setR} suf="%"/>
    <Div/>
    <RRow label="Years to College" value={`${res.yrs} years`}/><RRow label="Future Cost" value={fmt(res.fut)} hi color={T.accent}/><RRow label="Monthly SIP Needed" value={fmt(res.sip)} hi color={T.secondary}/><RRow label="Lumpsum Needed Today" value={fmt(res.lumpsum)}/>
  </div>);
}

function CompoundInterestCalc() {
  const PRESETS=[{l:"Savings A/c",r:4,f:"4"},{l:"Fixed Deposit",r:7,f:"4"},{l:"Equity MF",r:12,f:"1"},{l:"PPF",r:7.1,f:"1"}];
  const FMAP={"365":"Daily","12":"Monthly","4":"Quarterly","2":"Semi-annually","1":"Annually"};
  const [init,setInit]=useState(10000); const [monthly,setMonthly]=useState(500);
  const [Y,setY]=useState(10); const [R,setR]=useState(8); const [freq,setFreq]=useState("12");
  const [inf,setInf]=useState(5.09); const [showInf,setShowInf]=useState(true);
  const [taxOn,setTaxOn]=useState(false); const [tb,setTb]=useState(30); const [tt,setTt]=useState("debt");
  const [showTable,setShowTable]=useState(false);
  const res=useMemo(()=>{
    const n=parseFloat(freq),r=R/100,mr2=r/12;
    const lumpsumFV=init*Math.pow(1+r/n,n*Y);
    const contribFV=monthly>0?monthly*((Math.pow(1+mr2,12*Y)-1)/mr2):0;
    const tot=lumpsumFV+contribFV;
    const totContrib=init+monthly*12*Y;
    const interest=tot-totContrib;
    const inflAdj=tot/Math.pow(1+inf/100,Y);
    const ear=(Math.pow(1+r/n,n)-1)*100;
    const tax=calcTaxOnReturns(interest,tb,tt);
    const postTax=tot-tax, postTaxInflAdj=postTax/Math.pow(1+inf/100,Y);
    let bal=init,tInv=init;
    const table=Array.from({length:Y},(_,i)=>{
      const start=bal;const contrib=monthly*12;
      bal=bal*Math.pow(1+r/n,n)+monthly*((Math.pow(1+mr2,12)-1)/mr2);
      tInv+=contrib;
      return {year:i+1,start:Math.round(start),contrib,interest:Math.round(bal-start-contrib),end:Math.round(bal),inflAdj:Math.round(bal/Math.pow(1+inf/100,i+1))};
    });
    const lineData=table.map(d=>({year:`Y${d.year}`,"Contributions":init+monthly*12*d.year,"Future Value":d.end,...(showInf?{"Inflation-Adjusted":d.inflAdj}:{})}));
    return {tot,totContrib,interest,inflAdj,ear,tax,postTax,postTaxInflAdj,table,lineData};
  },[init,monthly,Y,R,freq,inf,tb,tt,showInf]);
  return(<div>
    <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
      {PRESETS.map(p=>(
        <button key={p.l} onClick={()=>{setR(p.r);setFreq(p.f);}} style={{padding:"4px 10px",borderRadius:20,fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${T.border}`,background:T.bg,color:T.sub}}>{p.l}</button>
      ))}
    </div>
    <Sl label="Initial Investment" min={1000} max={10000000} step={1000} value={init} onChange={setInit} pre="₹"/>
    <Sl label="Monthly Contribution" min={0} max={1000000} step={500} value={monthly} onChange={setMonthly} pre="₹"/>
    <Sl label="Years to Grow" min={1} max={50} value={Y} onChange={setY} suf=" yrs"/>
    <Sl label="Interest Rate" min={.5} max={30} step={.1} value={R} onChange={setR} suf="%"/>
    <Sel label="Compound Frequency" value={freq} onChange={setFreq} options={Object.entries(FMAP).map(([v,l])=>({v,l}))}/>
    <Sl label="Inflation Rate" min={1} max={15} step={.01} value={inf} onChange={setInf} suf="%"/>
    <Toggle label="Show inflation-adjusted" value={showInf} onChange={setShowInf} color="#ff8a65"/>
    <TaxPanel on={taxOn} setOn={setTaxOn} bracket={tb} setBracket={setTb} type={tt} setType={setTt}/>
    <Div/>
    <RRow label="Total Contributions" value={fmt(res.totContrib)}/><RRow label="Interest Earned" value={fmt(res.interest)}/><RRow label="Future Value" value={fmt(res.tot)} hi/><RRow label={`EAR (${FMAP[freq]})`} value={pct(res.ear)}/>
    {showInf&&<RRow label="Inflation-Adjusted" value={fmt(res.inflAdj)} hi color="#ff8a65"/>}
    {taxOn&&<><RRow label="Tax" value={fmt(res.tax)} color={T.danger}/><RRow label="Post-tax Value" value={fmt(res.postTax)} hi color={T.purple}/>{showInf&&<RRow label="Post-tax Inflation-Adjusted" value={fmt(res.postTaxInflAdj)} indent/>}</>}
    <Div/>
    <ResponsiveContainer width="100%" height={170}>
      <LineChart data={res.lineData}>
        <XAxis dataKey="year" tick={{fill:T.sub,fontSize:10}} axisLine={false} tickLine={false} interval={Math.floor(Y/5)}/>
        <YAxis tickFormatter={fmt} tick={{fill:T.sub,fontSize:9}} axisLine={false} tickLine={false} width={55}/>
        <Tooltip content={<CTip/>}/>
        <Line type="monotone" dataKey="Contributions" stroke={PALETTE[0]} dot={false} strokeWidth={1.5} strokeDasharray="4 2"/>
        <Line type="monotone" dataKey="Future Value" stroke={PALETTE[1]} dot={false} strokeWidth={2.5}/>
        {showInf&&<Line type="monotone" dataKey="Inflation-Adjusted" stroke="#ff8a65" dot={false} strokeWidth={1.5} strokeDasharray="3 3"/>}
        <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
      </LineChart>
    </ResponsiveContainer>
    <button onClick={()=>setShowTable(!showTable)} style={{width:"100%",marginTop:8,padding:"6px 0",background:"transparent",border:`1px solid ${T.border}`,borderRadius:7,color:T.sub,fontSize:10,cursor:"pointer",fontWeight:700}}>{showTable?"▲ Hide":"▼ Show"} Year-by-Year Table</button>
    {showTable&&<div style={{overflowX:"auto",marginTop:8}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
        <thead><tr style={{color:T.sub,borderBottom:`1px solid ${T.border}`}}>
          {["Yr","Start","Contrib","Interest","End"].map(h=><th key={h} style={{padding:"5px 6px",textAlign:"right",fontWeight:700}}>{h}</th>)}
        </tr></thead>
        <tbody>{res.table.map(r=><tr key={r.year} style={{borderBottom:`1px solid ${T.border}22`}}>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.sub}}>{r.year}</td>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.text}}>{fmt(r.start)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.purple}}>{fmt(r.contrib)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",color:T.secondary}}>{fmt(r.interest)}</td>
          <td style={{padding:"4px 6px",textAlign:"right",fontWeight:800,color:T.text}}>{fmt(r.end)}</td>
        </tr>)}</tbody>
      </table>
    </div>}
  </div>);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CALCULATOR REGISTRY
// ═══════════════════════════════════════════════════════════════════════════════
const CATEGORIES = [
  {id:"invest",icon:"📊",label:"Investment & Wealth",color:"#00c2ff",calcs:[
    {id:"sip",label:"SIP Calculator",desc:"Mutual fund SIP with step-up",icon:"📈",comp:SIPCalc},
    {id:"lumpsum",label:"Lumpsum Investment",desc:"Single investment growth",icon:"💰",comp:LumpsumCalc},
    {id:"swp",label:"SWP Calculator",desc:"Systematic Withdrawal Plan",icon:"🔄",comp:SWPCalc},
    {id:"stp",label:"STP Calculator",desc:"Systematic Transfer Plan",icon:"↔️",comp:STPCalc},
    {id:"cagr",label:"CAGR Calculator",desc:"Compound Annual Growth Rate",icon:"📊",comp:CAGRCalc},
    {id:"absreturn",label:"Absolute Return",desc:"Simple return calculation",icon:"💹",comp:AbsReturnCalc},
    {id:"xirr",label:"XIRR Calculator",desc:"Multiple cash flow returns",icon:"🔢",comp:XIRRCalc},
    {id:"goal",label:"Goal-Based Planning",desc:"SIP needed for your goal",icon:"🎯",comp:GoalCalc},
    {id:"epf",label:"EPF Calculator",desc:"Provident fund corpus",icon:"👷",comp:EPFCalc},
    {id:"nps",label:"NPS Calculator",desc:"National Pension System",icon:"🏛️",comp:NPSCalc},
    {id:"ssy",label:"SSY Calculator",desc:"Sukanya Samriddhi Yojana",icon:"👧",comp:SSYCalc},
    {id:"fd",label:"FD Calculator",desc:"Fixed deposit returns",icon:"🏦",comp:FDCalc},
    {id:"ppf",label:"PPF Calculator",desc:"Public Provident Fund",icon:"🛡️",comp:PPFCalc},
    {id:"rd",label:"RD Calculator",desc:"Recurring deposit",icon:"💳",comp:RDCalc},
    {id:"compound",label:"Compound Interest",desc:"With contribution & frequency",icon:"⚗️",comp:CompoundInterestCalc},
  ]},
  {id:"loan",icon:"🏠",label:"Loan & Debt",color:"#00e5a0",calcs:[
    {id:"homeloan",label:"Home Loan EMI",desc:"With amortization & tax benefits",icon:"🏠",comp:HomeLoanCalc},
    {id:"personalloan",label:"Personal Loan",desc:"EMI & total cost",icon:"👤",comp:()=><GenLoanCalc title="Personal Loan" defR={13} defY={3} defP={300000}/>},
    {id:"carloan",label:"Car Loan",desc:"Total cost of ownership",icon:"🚗",comp:CarLoanCalc},
    {id:"goldloan",label:"Gold Loan",desc:"LTV & repayment",icon:"🥇",comp:GoldLoanCalc},
    {id:"bizloan",label:"Business Loan",desc:"EMI & effective cost",icon:"🏢",comp:()=><GenLoanCalc title="Business Loan" defR={15} defY={5} defP={1000000}/>},
    {id:"baltransfer",label:"Balance Transfer",desc:"Is it worth transferring?",icon:"🔀",comp:BalTransferCalc},
    {id:"debtconsolidate",label:"Debt Consolidation",desc:"Multiple debts → one EMI",icon:"🗂️",comp:DebtConsolidationCalc},
    {id:"creditcard",label:"Credit Card Payoff",desc:"Fastest debt payoff",icon:"💳",comp:CreditCardPayoffCalc},
    {id:"loanafford",label:"Loan Affordability",desc:"How much can you borrow?",icon:"✅",comp:LoanAffordCalc},
  ]},
  {id:"insurance",icon:"🛡️",label:"Insurance",color:"#b388ff",calcs:[
    {id:"termins",label:"Term Insurance Need",desc:"How much coverage you need",icon:"🛡️",comp:TermInsCalc},
    {id:"ulip",label:"ULIP Calculator",desc:"ULIP vs Term+MF comparison",icon:"📋",comp:ULIPCalc},
  ]},
  {id:"tax",icon:"💰",label:"Tax",color:"#ffb800",calcs:[
    {id:"incometax",label:"Income Tax",desc:"Old vs New regime",icon:"📋",comp:IncomeTaxCalc},
    {id:"capgains",label:"Capital Gains Tax",desc:"Equity, Debt & Property",icon:"📈",comp:CapGainsTaxCalc},
    {id:"hra",label:"HRA Calculator",desc:"House rent allowance",icon:"🏡",comp:HRACalc},
    {id:"80c",label:"80C Deductions",desc:"Maximize tax savings",icon:"💡",comp:D80CCalc},
    {id:"gst",label:"GST Calculator",desc:"Add or remove GST",icon:"🧾",comp:GSTCalc},
    {id:"cryptotax",label:"Crypto Tax (VDA)",desc:"30% flat India crypto tax",icon:"₿",comp:CryptoTaxCalc},
  ]},
  {id:"retirement",icon:"🏖️",label:"Retirement & Inflation",color:"#ff8a65",calcs:[
    {id:"retcorpus",label:"Retirement Corpus",desc:"How much you need to retire",icon:"🏖️",comp:RetirementCorpusCalc},
    {id:"fire",label:"FIRE Calculator",desc:"Retire early planning",icon:"🔥",comp:FIRECalc},
    {id:"inflation",label:"Inflation Calculator",desc:"Purchasing power erosion",icon:"💸",comp:InflationCalc},
    {id:"realreturn",label:"Real Return",desc:"Nominal return minus inflation",icon:"📉",comp:RealReturnCalc},
  ]},
  {id:"realestate",icon:"🏡",label:"Real Estate",color:"#4db6ac",calcs:[
    {id:"rentvsbuy",label:"Rent vs Buy",desc:"Which is better for you?",icon:"⚖️",comp:RentVsBuyCalc},
    {id:"rentalyield",label:"Rental Yield",desc:"Net & gross yield",icon:"🏢",comp:RentalYieldCalc},
    {id:"stampduty",label:"Stamp Duty",desc:"State-wise charges",icon:"📜",comp:StampDutyCalc},
  ]},
  {id:"salary",icon:"💼",label:"Salary & Compensation",color:"#29b6f6",calcs:[
    {id:"inhand",label:"In-Hand Salary",desc:"CTC to take-home",icon:"💼",comp:InHandCalc},
    {id:"gratuity",label:"Gratuity Calculator",desc:"Retirement gratuity",icon:"🎁",comp:GratuityCalc},
    {id:"hike",label:"Salary Hike",desc:"Increment calculator",icon:"⬆️",comp:SalaryHikeCalc},
  ]},
  {id:"trading",icon:"📈",label:"Trading & Crypto",color:"#ff4d6d",calcs:[
    {id:"brokerage",label:"Brokerage Calculator",desc:"Compare Zerodha, Groww, ICICI",icon:"🏦",comp:BrokerageCalc},
    {id:"positionsize",label:"Position Sizing",desc:"Risk-based trade size",icon:"🎯",comp:PositionSizingCalc},
    {id:"options",label:"Options P&L",desc:"Call & Put payoff diagram",icon:"📊",comp:OptionsCalc},
    {id:"cryptotaxtrading",label:"Crypto P&L & Tax",desc:"VDA 30% tax calculator",icon:"₿",comp:CryptoTaxCalc},
  ]},
  {id:"gold",icon:"🪙",label:"Gold & Commodities",color:"#ffb800",calcs:[
    {id:"goldinvest",label:"Gold Investment",desc:"Returns & CAGR",icon:"🥇",comp:GoldCalc},
    {id:"sgb",label:"Sovereign Gold Bond",desc:"SGB maturity calculator",icon:"📜",comp:SGBCalc},
    {id:"goldloan2",label:"Gold Loan",desc:"Loan against gold",icon:"💰",comp:GoldLoanCalc},
  ]},
  {id:"misc",icon:"🔧",label:"Miscellaneous",color:"#94a3b8",calcs:[
    {id:"emergency",label:"Emergency Fund",desc:"Safety net calculator",icon:"🚨",comp:EmergencyFundCalc},
    {id:"college",label:"College Cost Planner",desc:"Education savings goal",icon:"🎓",comp:CollegeCostCalc},
    {id:"rule72",label:"Rule of 72",desc:"How long to double money",icon:"✌️",comp:Rule72Calc},
    {id:"dti",label:"Debt-to-Income Ratio",desc:"Your financial health score",icon:"📊",comp:DTICalc},
    {id:"compound2",label:"Compound Interest",desc:"Universal CI calculator",icon:"⚗️",comp:CompoundInterestCalc},
  ]},
];

const ALL_CALCS = CATEGORIES.flatMap(cat => cat.calcs.map(c => ({...c, catId:cat.id, catLabel:cat.label, catColor:cat.color})));

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function CalcKaro() {
  const [view,setView]=useState("home"); // home | category | calc
  const [activeCat,setActiveCat]=useState(null);
  const [activeCalc,setActiveCalc]=useState(null);
  const [search,setSearch]=useState("");
  const [menuOpen,setMenuOpen]=useState(false);

  const searchResults=useMemo(()=>{
    if(!search.trim()) return [];
    const q=search.toLowerCase();
    return ALL_CALCS.filter(c=>c.label.toLowerCase().includes(q)||c.desc.toLowerCase().includes(q)||c.catLabel.toLowerCase().includes(q)).slice(0,8);
  },[search]);

  const openCalc=(calc)=>{setActiveCalc(calc);setView("calc");setSearch("");setMenuOpen(false);};
  const openCat=(cat)=>{setActiveCat(cat);setView("category");setMenuOpen(false);};
  const goHome=()=>{setView("home");setActiveCat(null);setActiveCalc(null);setSearch("");};

  const totalCalcs=ALL_CALCS.length;

  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'Segoe UI','DM Sans',system-ui,sans-serif"}}>

      {/* ── HEADER ── */}
      <header style={{
        position:"sticky",top:0,zIndex:200,
        background:T.bg+"e8",backdropFilter:"blur(16px)",
        borderBottom:`1px solid ${T.border}`,
        padding:"0 16px",height:52,
        display:"flex",alignItems:"center",gap:10,
      }}>
        <button onClick={goHome} style={{background:"none",border:"none",cursor:"pointer",padding:0,flexShrink:0}}>
          <span style={{fontSize:18,fontWeight:900,letterSpacing:-1,color:T.text}}>
            Calc<span style={{color:T.primary}}>Karo</span>
          </span>
          <sup style={{fontSize:8,color:T.muted,marginLeft:2}}>.in</sup>
        </button>

        {/* Search */}
        <div style={{flex:1,position:"relative",maxWidth:260}}>
          <input
            value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search calculators..."
            style={{width:"100%",background:T.card,border:`1px solid ${T.border}`,borderRadius:20,
              color:T.text,fontSize:12,padding:"6px 12px 6px 30px",outline:"none",boxSizing:"border-box"}}
          />
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:13,color:T.muted}}>🔍</span>
          {searchResults.length>0&&(
            <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,zIndex:300,overflow:"hidden",boxShadow:"0 8px 32px #000a"}}>
              {searchResults.map(c=>(
                <button key={c.id} onClick={()=>openCalc(c)} style={{
                  display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 12px",
                  background:"none",border:"none",cursor:"pointer",textAlign:"left",
                  borderBottom:`1px solid ${T.border}22`,
                }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.primary+"11"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <span style={{fontSize:16}}>{c.icon}</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>{c.label}</div>
                    <div style={{fontSize:10,color:T.muted}}>{c.catLabel}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── NAV LINKS ── */}
        <div style={{display:"flex",alignItems:"center",gap:4,marginLeft:"auto",flexShrink:0}}>

          {/* Calculators dropdown */}
          <div style={{position:"relative"}}
            onMouseEnter={e=>e.currentTarget.querySelector('.dd').style.display='block'}
            onMouseLeave={e=>e.currentTarget.querySelector('.dd').style.display='none'}>
            <button style={{background:"none",border:`1px solid ${T.border}`,borderRadius:20,
              color:T.sub,fontSize:11,padding:"5px 10px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:4,fontWeight:600}}>
              📐 Calculators <span style={{fontSize:8}}>▾</span>
            </button>
            <div className="dd" style={{
              display:"none",position:"absolute",top:"100%",right:0,
              background:T.card,border:`1px solid ${T.border}`,
              borderRadius:10,padding:"8px",zIndex:300,
              boxShadow:"0 8px 32px #000c",minWidth:200,marginTop:4,
            }}>
              {CATEGORIES.map(cat=>(
                <button key={cat.id} onClick={()=>openCat(cat)}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",background:"none",border:"none",
                    cursor:"pointer",textAlign:"left",borderRadius:6,color:T.text}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.primary+"11"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <span style={{fontSize:16}}>{cat.icon}</span>
                  <div>
                    <div style={{fontSize:11,fontWeight:700}}>{cat.label}</div>
                    <div style={{fontSize:9,color:T.muted}}>{cat.calcs.length} calculators</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tools dropdown */}
          <div style={{position:"relative"}}
            onMouseEnter={e=>e.currentTarget.querySelector('.dd2').style.display='block'}
            onMouseLeave={e=>e.currentTarget.querySelector('.dd2').style.display='none'}>
            <button style={{background:T.primary+"18",border:`1px solid ${T.primary}44`,borderRadius:20,
              color:T.primary,fontSize:11,padding:"5px 10px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:4,fontWeight:700}}>
              🛠️ Tools <span style={{fontSize:8}}>▾</span>
            </button>
            <div className="dd2" style={{
              display:"none",position:"absolute",top:"100%",right:0,
              background:T.card,border:`1px solid ${T.border}`,
              borderRadius:10,padding:"8px",zIndex:300,
              boxShadow:"0 8px 32px #000c",minWidth:220,marginTop:4,
            }}>
              {[
                {icon:"📊",label:"Portfolio Backtester",desc:"Test any portfolio vs Nifty 50",url:"/backtester/",badge:""},
                {icon:"🎨",label:"Sector Returns Matrix",desc:"Which sector won each year",url:"/sector-matrix/",badge:""},
                {icon:"💯",label:"Financial Health Score",desc:"Score your financial health",url:"/health-score/",badge:"New"},
                {icon:"💳",label:"Credit Card Trap",desc:"True cost of minimum payments",url:"/credit-card-trap/",badge:"Soon"},
                {icon:"📉",label:"Inflation Destroyer",desc:"What your money really buys",url:"/inflation-destroyer/",badge:"Soon"},
                {icon:"🎯",label:"Am I Saving Enough?",desc:"Are you on track?",url:"/savings-check/",badge:"Soon"},
              ].map(t=>(
                <a key={t.url} href={t.url}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",textDecoration:"none",borderRadius:6,color:T.text}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.primary+"11"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <span style={{fontSize:16}}>{t.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                      {t.label}
                      {t.badge&&<span style={{fontSize:8,background:t.badge==="New"?T.primary:"#333",color:"#fff",padding:"1px 5px",borderRadius:20,fontWeight:700}}>{t.badge}</span>}
                    </div>
                    <div style={{fontSize:9,color:T.muted}}>{t.desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {view!=="home"&&<button onClick={goHome} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:20,color:T.sub,fontSize:11,padding:"5px 10px",cursor:"pointer"}}>⌂</button>}
        </div>
      </header>
      {/* ── VIEWS ── */}
      {view==="home"&&(
        <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 80px"}}>
          {/* Hero */}
          <div style={{textAlign:"center",padding:"28px 0 36px"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,background:T.primary+"18",border:`1px solid ${T.primary}33`,borderRadius:20,padding:"4px 14px",fontSize:10,color:T.primary,fontWeight:700,marginBottom:14,letterSpacing:.5}}>
              ⚡ FREE · ACCURATE · INFLATION & TAX AWARE
            </div>
            <h1 style={{fontSize:"clamp(24px,5vw,38px)",fontWeight:900,margin:"0 0 10px",letterSpacing:-1.5,lineHeight:1.1}}>
              India's Most Complete<br/>
              <span style={{background:`linear-gradient(90deg,${T.primary},${T.secondary})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>Financial Calculator</span>
            </h1>
            <p style={{fontSize:14,color:T.sub,margin:"0 0 20px",lineHeight:1.6}}>
              {totalCalcs}+ calculators across {CATEGORIES.length} categories.<br/>Built for Indian investors, taxpayers & borrowers.
            </p>
            {/* Feature pills */}
            <div style={{display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center"}}>
              {["📉 Inflation Toggle","💸 Tax Toggle","📊 Live Charts","📋 Year-wise Tables","⚡ Instant Results"].map(f=>(
                <span key={f} style={{fontSize:10,color:T.sub,background:T.card,border:`1px solid ${T.border}`,borderRadius:20,padding:"3px 10px"}}>{f}</span>
              ))}
            </div>
          </div>

          {/* Quick Access */}
          <div style={{marginBottom:28}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:800,letterSpacing:1,marginBottom:10}}>POPULAR CALCULATORS</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8}}>
              {[{id:"sip",cat:"invest"},{id:"homeloan",cat:"loan"},{id:"incometax",cat:"tax"},{id:"fd",cat:"invest"},{id:"compound",cat:"invest"},{id:"fire",cat:"retirement"}].map(({id,cat})=>{
                const c=ALL_CALCS.find(x=>x.id===id||x.id===id);
                if(!c) return null;
                return(
                  <button key={id} onClick={()=>openCalc(c)} style={{
                    background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
                    padding:"12px 10px",cursor:"pointer",textAlign:"left",color:T.text,
                    transition:"border-color .15s,transform .15s",
                  }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.primary;e.currentTarget.style.transform="translateY(-2px)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
                    <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
                    <div style={{fontSize:11,fontWeight:700}}>{c.label}</div>
                    <div style={{fontSize:9,color:T.muted,marginTop:2}}>{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
{/* Tools */}
<div style={{marginBottom:28}}>
  <div style={{fontSize:10,color:T.muted,fontWeight:800,letterSpacing:1,marginBottom:10}}>FREE TOOLS</div>
  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>

<a href="/health-score/" style={{textDecoration:"none"}}
  onMouseEnter={e=>{e.currentTarget.style.borderColor="#10b981";e.currentTarget.style.transform="translateY(-2px)";}}
  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
  <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 14px",cursor:"pointer",textAlign:"left",color:T.text,transition:"border-color .15s,transform .15s"}}>
    <div style={{fontSize:28,marginBottom:8}}>💯</div>
    <div style={{fontSize:13,fontWeight:700,marginBottom:3}}>Financial Health Score</div>
    <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>Score your finances out of 100 · Get action plan</div>
    <div style={{height:2,background:"linear-gradient(90deg,#10b98144,transparent)",marginTop:8,borderRadius:1}}/>
  </div>
</a>
    <a href="/backtester/" style={{textDecoration:"none"}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor="#7c6dfa";e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 14px",cursor:"pointer",textAlign:"left",color:T.text,transition:"border-color .15s,transform .15s"}}>
        <div style={{fontSize:28,marginBottom:8}}>📊</div>
        <div style={{fontSize:13,fontWeight:700,marginBottom:3}}>Portfolio Backtester</div>
        <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>Test any portfolio vs Nifty 50 · 15 years of data</div>
        <div style={{height:2,background:"linear-gradient(90deg,#7c6dfa44,transparent)",marginTop:8,borderRadius:1}}/>
      </div>
    </a>

    <a href="/sector-matrix/" style={{textDecoration:"none"}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor="#f59e0b";e.currentTarget.style.transform="translateY(-2px)";}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"16px 14px",cursor:"pointer",textAlign:"left",color:T.text,transition:"border-color .15s,transform .15s"}}>
        <div style={{fontSize:28,marginBottom:8}}>🎨</div>
        <div style={{fontSize:13,fontWeight:700,marginBottom:3}}>Sector Returns Matrix</div>
        <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>India sector quilt · 2010–2025 · Who won each year</div>
        <div style={{height:2,background:"linear-gradient(90deg,#f59e0b44,transparent)",marginTop:8,borderRadius:1}}/>
      </div>
    </a>

  </div>
</div>
          {/* All Categories */}
          <div style={{fontSize:10,color:T.muted,fontWeight:800,letterSpacing:1,marginBottom:10}}>ALL CATEGORIES</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
            {CATEGORIES.map(cat=>(
              <button key={cat.id} onClick={()=>openCat(cat)} style={{
                background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
                padding:"16px 14px",cursor:"pointer",textAlign:"left",color:T.text,
                transition:"border-color .15s,transform .15s",
              }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=cat.color;e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
                <div style={{fontSize:28,marginBottom:8}}>{cat.icon}</div>
                <div style={{fontSize:13,fontWeight:700,marginBottom:3}}>{cat.label}</div>
                <div style={{fontSize:10,color:T.muted}}>{cat.calcs.length} calculators</div>
                <div style={{height:2,background:`linear-gradient(90deg,${cat.color}44,transparent)`,marginTop:8,borderRadius:1}}/>
              </button>
            ))}
          </div>

          <div style={{textAlign:"center",marginTop:40,color:T.muted,fontSize:10,lineHeight:1.7}}>
            Results are indicative, not guaranteed. Consult a financial advisor for personalized advice.<br/>
            CalcKaro © 2025 · Made with ❤️ in Mumbai · <span style={{color:T.primary}}>info@calckaro.in</span>
          </div>
        </div>
      )}

      {view==="category"&&activeCat&&(
        <div style={{maxWidth:660,margin:"0 auto",padding:"20px 16px 80px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24}}>
            <div style={{width:44,height:44,borderRadius:12,background:activeCat.color+"22",border:`1px solid ${activeCat.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{activeCat.icon}</div>
            <div>
              <div style={{fontSize:20,fontWeight:800}}>{activeCat.label}</div>
              <div style={{fontSize:11,color:T.sub}}>{activeCat.calcs.length} calculators</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10}}>
            {activeCat.calcs.map(calc=>(
              <button key={calc.id} onClick={()=>openCalc({...calc,catColor:activeCat.color,catLabel:activeCat.label})} style={{
                background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
                padding:"14px 12px",cursor:"pointer",textAlign:"left",color:T.text,
                transition:"border-color .15s,transform .15s",
              }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=activeCat.color;e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="none";}}>
                <div style={{fontSize:24,marginBottom:7}}>{calc.icon}</div>
                <div style={{fontSize:12,fontWeight:700,marginBottom:3}}>{calc.label}</div>
                <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>{calc.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {view==="calc"&&activeCalc&&(()=>{
        const Comp=activeCalc.comp;
        return(
          <div style={{maxWidth:500,margin:"0 auto",padding:"18px 16px 80px"}}>
            <div style={{display:"flex",gap:3,marginBottom:16,flexWrap:"wrap"}}>
              {activeCalc.catLabel&&(
                <button onClick={()=>{const c=CATEGORIES.find(x=>x.id===activeCalc.catId);if(c)openCat(c);else goHome();}} style={{background:"none",border:"none",color:T.muted,fontSize:11,cursor:"pointer",padding:"0 4px 0 0"}}>
                  {activeCalc.catLabel} →
                </button>
              )}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
              <div style={{width:42,height:42,borderRadius:11,background:(activeCalc.catColor||T.primary)+"22",border:`1px solid ${(activeCalc.catColor||T.primary)}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{activeCalc.icon}</div>
              <div>
                <div style={{fontSize:18,fontWeight:800}}>{activeCalc.label}</div>
                <div style={{fontSize:11,color:T.sub}}>{activeCalc.desc}</div>
              </div>
            </div>
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px 16px"}}>
              <Comp/>
            </div>
            <div style={{textAlign:"center",marginTop:12,fontSize:9,color:T.muted}}>
              Results are indicative, not guaranteed. Inflation: 5.09% (India CPI). Tax rates as per FY 2024-25.
            </div>
            {/* Related calcs in same category */}
            {activeCalc.catId&&(()=>{
              const related=ALL_CALCS.filter(c=>c.catId===activeCalc.catId&&c.id!==activeCalc.id).slice(0,3);
              if(!related.length) return null;
              return(<div style={{marginTop:20}}>
                <div style={{fontSize:10,color:T.muted,fontWeight:700,marginBottom:8}}>MORE IN {activeCalc.catLabel?.toUpperCase()}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {related.map(r=>(
                    <button key={r.id} onClick={()=>openCalc(r)} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",background:T.card,border:`1px solid ${T.border}`,borderRadius:20,color:T.sub,fontSize:11,cursor:"pointer",fontWeight:600}}>
                      <span>{r.icon}</span>{r.label}
                    </button>
                  ))}
                </div>
              </div>);
            })()}
          </div>
        );
      })()}

    </div>
  );
}
